import { toast } from "@/components/common/Toast"
import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import type { QueryClient } from "@tanstack/react-query"
import {
  isCancelledError,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import type { AxiosRequestConfig } from "axios"
import { useCallback } from "react"
import {
  convertDateStringsToDates,
  convertKeysToCamelCase,
} from "../../../services/adapters/caseConversion/caseConversions"
import { apiClientV2 } from "../../../services/api/apiClient"
import { normalizeCas } from "../components/declarations/review/studio-v2/mapping/conditionRowMerge"
import { getApiErrorCode, getApiErrorStatus, toastApiError } from "./apiError"
import { REVIEW_NOW_MUTATION_KEY } from "./mutationKeys"
import { announceRecompute } from "./recomputeSignal"
import type {
  ApplyStatementPoolScopeIn,
  ApprovePreview,
  ApproveResult,
  Article,
  ConcentrationMemoryHint,
  ConditionFieldRow,
  ConditionFieldRowsOut,
  CreatedStatement,
  CreateStatementIn,
  Declaration,
  DeclarationArticleStates,
  DeclarationAuditEvent,
  DeclarationConditionFields,
  DeclarationExtractionOut,
  DeclarationFacets,
  DeclarationLinkedArticle,
  DeclarationMapping,
  DeclarationOpenItemsWire,
  DeclarationReadModel,
  DeclarationScopeOut,
  DeclarationSortBy,
  DeclarationSortDir,
  DeclarationStats,
  EditStatementVerdictIn,
  LifecycleStatus,
  ReviewFieldIn,
  ReviewStatementIn,
  SaveConditionFieldsIn,
  StatementGroup,
  StatementGroupEntry,
  Supplier,
  UpdateScopeIn,
  UpdateStatementScopeIn,
  VerifyStatementIn,
} from "./types"
import type { PaginatedOut } from "./types/masterData"

// ─── Mock data ────────────────────────────────────────────────────────────────
// MOCK_SUPPLIERS remains for the legacy supplier list until that endpoint is wired.

const MOCK_SUPPLIERS: Supplier[] = [
  {
    id: "0011335",
    name: "micronetics GmbH",
    country: "DE",
    vat: "DE264839117",
    contact: "Stefan Brandt",
    status: "active",
    lastDeclaration: "2025-03-14",
    complianceStatus: "compliant",
    criticality: "high",
    articleCount: 24,
  },
  {
    id: "0010540",
    name: "Helukabel GmbH",
    country: "DE",
    vat: "DE145908273",
    contact: "Petra Wagner",
    status: "active",
    lastDeclaration: "2024-11-02",
    complianceStatus: "compliant",
    criticality: "high",
    articleCount: 38,
  },
  {
    id: "0070171",
    name: "MEDI Kabel GmbH",
    country: "DE",
    vat: "DE218236650",
    contact: "Marlene Hofer",
    status: "active",
    lastDeclaration: "2024-09-30",
    complianceStatus: "review",
    criticality: "medium",
    articleCount: 14,
  },
  {
    id: "0033412",
    name: "AL-Elektronik / Morecrafts",
    country: "TW",
    vat: "TW28114902",
    contact: "Clark Yang",
    status: "active",
    lastDeclaration: "2025-07-01",
    complianceStatus: "caveat",
    criticality: "medium",
    articleCount: 11,
  },
  {
    id: "0019887",
    name: "Celanese Sales Germany GmbH",
    country: "DE",
    vat: "DE145018567",
    contact: "HazCom Compliance",
    status: "active",
    lastDeclaration: "2024-10-24",
    complianceStatus: "compliant",
    criticality: "high",
    articleCount: 6,
  },
  {
    id: "0044120",
    name: "Gillich Kunststoffe GmbH",
    country: "DE",
    vat: "DE192881214",
    contact: "Tobias Gillich",
    status: "active",
    lastDeclaration: "2024-12-11",
    complianceStatus: "compliant",
    criticality: "medium",
    articleCount: 9,
  },
  {
    id: "0044908",
    name: "Lapp Kabel Stuttgart GmbH",
    country: "DE",
    vat: "DE147331140",
    contact: "Andreas Becker",
    status: "active",
    lastDeclaration: "2025-04-02",
    complianceStatus: "compliant",
    criticality: "medium",
    articleCount: 21,
  },
  {
    id: "0055019",
    name: "Würth Industrie Service",
    country: "DE",
    vat: "DE158168440",
    contact: "Sabine Roth",
    status: "active",
    lastDeclaration: "2024-10-04",
    complianceStatus: "compliant",
    criticality: "low",
    articleCount: 156,
  },
  {
    id: "0055210",
    name: "Schaeffler AG",
    country: "DE",
    vat: "DE218236650",
    contact: "Marc Lehmann",
    status: "review",
    lastDeclaration: "2024-08-19",
    complianceStatus: "review",
    criticality: "medium",
    articleCount: 18,
  },
  {
    id: "0066330",
    name: "Phoenix Contact GmbH",
    country: "DE",
    vat: "DE119292430",
    contact: "Dr. Eva Bauer",
    status: "active",
    lastDeclaration: "2025-02-25",
    complianceStatus: "compliant",
    criticality: "high",
    articleCount: 19,
  },
  {
    id: "0072501",
    name: "Murata Electronics Europe",
    country: "DE",
    vat: "DE218892012",
    contact: "Akiko Nakamura",
    status: "active",
    lastDeclaration: "2025-03-01",
    complianceStatus: "compliant",
    criticality: "medium",
    articleCount: 11,
  },
  {
    id: "0088410",
    name: "ZF Friedrichshafen AG",
    country: "DE",
    vat: "DE145397277",
    contact: "Jürgen Schmitt",
    status: "active",
    lastDeclaration: "2023-09-30",
    complianceStatus: "expired",
    criticality: "low",
    articleCount: 7,
  },
]

// ─── API calls ────────────────────────────────────────────────────────────────

export interface ListDeclarationsParams {
  // Free-text search over supplier name, declaration number, framework and
  // file name (server-side, so a match beyond the loaded pages still shows).
  q?: string
  // Comma-separated list of lifecycle statuses (server-side filter).
  lifecycle?: string
  // Case-insensitive substring match on supplier name (server-side filter,
  // read-only deep-link — the dashboard's InReviewWidget links here with
  // `?supplier=...`, no dropdown control for it).
  supplier?: string
  // Single reply-quality bucket (server-side filter) — committed |
  // extraction_empty | conflicting. See F4-FE.
  quality?: string
  // Faceted toolbar filters (PR-C3) — each CSV-joined by the
  // caller the same way `lifecycle` above is, OR within one facet, AND across
  // facets, server-side. supplierIds is a CSV of supplier ids (not names);
  // docTypes/frameworks are CSVs of the facet's own filter value.
  supplierIds?: string
  docTypes?: string
  frameworks?: string
  // Held blank copies of our own inquiry files (TUR-1754): "exclude" leaves them out,
  // "only" shows them alone; absent keeps every row (what the review page needs).
  held?: "exclude" | "only"
  sortBy?: DeclarationSortBy
  sortDir?: DeclarationSortDir
}

export interface DeclarationsListPage {
  items: Declaration[]
  nextCursor: string | null
}

// In-flight rows (pending, processing) first, then by capturedAt descending.
// Applied once to the flattened set of all loaded pages (see
// useDeclarationsWithPolling callers) rather than per-page, since pagination
// would otherwise only ever sort within a single page — a pending/processing
// row that hasn't been paginated in yet still won't show near the top until
// its page loads; that limitation is inherent to sorting only what's loaded
// and isn't something a client-side re-sort can fix.
//
// `pending` and `processing` share bucket 0 (PR B / B4): a freshly queued row
// used to fall through to the catch-all 99 bucket below and could sink under
// every accepted/expiring row on a busy inbox, which read as "my upload went
// nowhere" even though it was simply unsorted rather than lost.
const LIFECYCLE_DISPLAY_ORDER: Record<string, number> = {
  pending: 0,
  processing: 0,
  ready: 1,
  expiring: 2,
  accepted: 3,
  // F10 — settled alongside 'accepted' (approved, processed, nothing left to
  // do but nudge the supplier for a fresh document): same family, not the
  // catch-all bucket a missing key would fall into.
  expired: 4,
}

export function sortDeclarationsForDisplay(rows: Declaration[]): Declaration[] {
  return [...rows].sort((a, b) => {
    const sa = LIFECYCLE_DISPLAY_ORDER[a.lifecycleStatus ?? ""] ?? 99
    const sb = LIFECYCLE_DISPLAY_ORDER[b.lifecycleStatus ?? ""] ?? 99
    if (sa !== sb) return sa - sb
    return (b.capturedAt ?? "").localeCompare(a.capturedAt ?? "")
  })
}

// V2's display-only counterpart (regression after main's PR #2014, which made
// DeclarationListV2 trust the server's received_at-desc order wholesale — see
// that component's `rows` comment for why: the server is the only thing that
// sees every page, not just what's been paginated in, so a client re-sort of
// `sortDeclarationsForDisplay`'s kind — full bucket ranking, capturedAt
// tie-break — would fight it). This is deliberately NOT a re-sort: a stable
// partition of the already-loaded rows into "in-flight" (pending/processing)
// first, everything else after, each bucket keeping its own server-assigned
// relative order untouched. Two `filter` passes rather than a `sort` +
// comparator so order-preservation is structural, not something a comparator
// could get subtly wrong. Only meant for the no-explicit-sort default — the
// caller leaves server order alone entirely once a sort is active.
function isInFlight(row: Declaration): boolean {
  return (
    row.lifecycleStatus === "pending" || row.lifecycleStatus === "processing"
  )
}

export function pinInFlightRows(rows: Declaration[]): Declaration[] {
  return [...rows.filter(isInFlight), ...rows.filter((r) => !isInFlight(r))]
}

const declarationsApiCalls = {
  listDeclarations: async (
    params?: ListDeclarationsParams,
    cursor?: string,
  ): Promise<DeclarationsListPage> => {
    // Fetch one page of the paginated declarations list; fall back to an
    // empty page on error.
    // q/lifecycle/supplier/quality/supplierIds/docTypes/frameworks/sortBy/sortDir
    // are all applied server-side so deep-linked filters aren't lost to the
    // page cap (see F4-FE) — never filter these client-side.
    return apiClientV2
      .get("/material-compliance/declarations", {
        params: {
          q: params?.q || undefined,
          lifecycle: params?.lifecycle || undefined,
          supplier: params?.supplier || undefined,
          quality: params?.quality || undefined,
          supplier_ids: params?.supplierIds || undefined,
          doc_types: params?.docTypes || undefined,
          frameworks: params?.frameworks || undefined,
          held: params?.held || undefined,
          sort_by: params?.sortBy || undefined,
          sort_dir: params?.sortDir || undefined,
          cursor: cursor || undefined,
        },
      })
      .then((r) => ({
        items: (r.data?.items ?? []) as Declaration[],
        nextCursor: (r.data?.nextCursor ?? null) as string | null,
      }))
      .catch(() => ({ items: [], nextCursor: null }))
  },

  listSuppliers: async (): Promise<Supplier[]> => {
    // TODO: return apiClient.get('/material-compliance/suppliers').then(r => r.data)
    return MOCK_SUPPLIERS
  },

  // Unfiltered, org-scoped inbox totals for the toolbar's facet dropdowns —
  // never scoped by the caller's own current selection, so picking a value in
  // one facet cannot shrink the options the others offer. Falls back to all
  // facets empty on error, same convention as the other list reads here.
  getDeclarationFacets: async (): Promise<DeclarationFacets> => {
    return apiClientV2
      .get("/material-compliance/declarations/facets")
      .then((r) => r.data as DeclarationFacets)
      .catch(
        (): DeclarationFacets => ({
          suppliers: [],
          docTypes: [],
          frameworks: [],
          lifecycle: [],
        }),
      )
  },

  getDeclarationStats: async (): Promise<DeclarationStats> => {
    // Fetch real stats; fall back to empty object on error.
    const realStats = await apiClientV2
      .get("/material-compliance/declarations/stats")
      .then((r) => r.data as Partial<DeclarationStats>)
      .catch(() => ({}) as Partial<DeclarationStats>)

    return {
      total: realStats.total ?? 0,
      expiringSoon: realStats.expiringSoon ?? 0,
      expired: realStats.expired ?? 0,
      awaitingReview: realStats.awaitingReview ?? 0,
      inProgress: realStats.inProgress ?? 0,
      acceptedLast30d: realStats.acceptedLast30d ?? 0,
      expiringWithin30d: realStats.expiringWithin30d ?? 0,
    }
  },
}

// ─── React Query hooks ────────────────────────────────────────────────────────

/**
 * Backend-mediated multipart upload of ONE declaration. The backend puts the file
 * to S3, creates the row and publishes the extraction event in one step; PDF and
 * Excel go through the same endpoint (it decides which from the bytes).
 *
 * A PLAIN FUNCTION, NOT A MUTATION HOOK. The import screen uploads a whole folder
 * through a bounded queue and reports each file's outcome itself, so it needs to
 * await one file at a time and keep each rejection's own message. It also
 * invalidates ONCE when the queue drains — a mutation with `onSuccess`
 * invalidation refetched the list (and the stat tiles behind it) once per file,
 * fifty times over a fifty-file intake.
 *
 * Rejections are thrown as-is so the caller can read `detail.code`, the
 * one thing translated into the UI's own words (`apiError.ts`'s
 * `API_ERROR_KEYS`); the server's own sentence never reaches the UI.
 */
export async function uploadDeclarationFile(file: File): Promise<Declaration> {
  const form = new FormData()
  form.append("file", file)
  return apiClientV2
    .post("/material-compliance/declarations/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data as Declaration)
}

/**
 * NO REFETCH ON A WINDOW REFOCUS for the reads the review screen is built from: the
 * interface-backed reads it mounts (`INTERFACE_READS` below, minus `articles`, which
 * only the list's side panel reads) and the source document.
 *
 * On the review page they move only through this app's own writes, which invalidate
 * them by prefix (`invalidateDeclaration`), and through the page's own hydration (when
 * the extraction lands and when a recompute settles, `useRefetchOnSettled`). A refocus
 * refetch can only re-download what is already on screen, and react-query's default
 * does it for every one of them at once: the prod test of 2026-10-02 (F5) counted five
 * full reloads in 80 s of tab switching, ~20 s of server work each on a 10k-article
 * declaration, the source PDF included. A surface WITHOUT that hydration (the side
 * panel's document preview) opts back in per observer.
 */
const REVIEW_READ_OPTIONS = { refetchOnWindowFocus: false } as const

/**
 * Fetch a declaration's source PDF via the backend stream endpoint.
 * GET /material-compliance/declarations/{id}/document returns the PDF as an
 * ArrayBuffer (avoids CORS issues with direct S3 URLs).
 *
 * `enabled` defaults to `true` so every existing caller (which only ever
 * passed `declId`) is unaffected; a caller that only learns which viewer the
 * bytes are for from a separate query (the read model) passes `false` until
 * that resolves, so the two requests don't race for no reason.
 */
export function useDeclarationDocument(
  declId: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["mc", "declarations", declId, "document"],
    queryFn: async (): Promise<ArrayBuffer | null> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declId}/document`, {
          responseType: "arraybuffer",
        })
        .then((r) => r.data as ArrayBuffer)
    },
    enabled: !!declId && enabled,
    // Binary payloads up to 50 MB — evict as soon as no viewer observes it,
    // instead of the default 5 minutes the cache would otherwise hold an
    // unmounted entry. The studio keeps an observer mounted while open, so
    // this costs no refetch churn there.
    gcTime: 0,
    ...REVIEW_READ_OPTIONS,
  })
}

export function useDeclarationStats() {
  return useQuery({
    queryKey: ["mc", "declarations", "stats"],
    queryFn: declarationsApiCalls.getDeclarationStats,
    // The list polls while anything is in flight; the tiles have to follow it or a
    // bulk import leaves "processing" frozen at whatever it was when the page opened.
    // The response says whether to keep asking, so the two never disagree about it.
    refetchInterval: (query) =>
      (query.state.data?.inProgress ?? 0) > 0 ? 3_000 : false,
  })
}

/**
 * The three toolbar facet dropdowns (supplier, doc type, framework) and the
 * lifecycle counts, unfiltered — see `getDeclarationFacets` above. Does not
 * poll: unlike the stat tiles, a facet list gaining/losing an option mid-review
 * is not the kind of change a reviewer needs to see land live, and a reopened
 * dropdown after a refetch-worthy gesture (upload, delete) already invalidates
 * the `["mc", "declarations"]` prefix this key sits under.
 */
export function useDeclarationFacets() {
  return useQuery({
    queryKey: ["mc", "declarations", "facets"],
    queryFn: declarationsApiCalls.getDeclarationFacets,
  })
}

// ─── Scenario data (design-time fixtures for classify → confirm flow) ─────────
// These represent the AI-extracted metadata per declaration pattern.
// In production this comes from the POST /classify response.

export const DECLARATION_SCENARIOS = {
  "correct-fill": {
    label: "Per-article template",
    doc: "micronetics_FMD_RoHS_REACH_2026.xlsx",
  },
  "blanket-pdf": {
    label: "Blanket statement",
    doc: "AL-Elektronik_Morecrafts_RoHS-REACH_New-20250701.pdf",
    blanketScope:
      "All products manufactured by Morecrafts Technology unless the product is specifically stated otherwise.",
    blanketStatement:
      "Morecrafts Technology hereby confirms that our products listed above provided to your company match the EU RoHS (2015/863/EU) latest requests, and Registration, Evaluation, Authorisation and Restriction of Chemicals (REACH). Revised Candidate SVHC 250-list addition date under REACH statement on Jun. 25th, 2025.",
    signatory: "Clark Yang · Sales Manager",
    signedDate: "Jul. 1st, 2025",
    affectedArticles: 11,
  },
  "own-article-numbers": {
    label: "Supplier's own codes",
    doc: "micronetics_Konformitaetserklaerung_2026.xlsx",
    detectedCodes: [
      "PV14-8RB-3K",
      "DNF14-250FIB-3K",
      "MICRO-PV14-8RY",
      "MICRO-XR-400",
      "MICRO-NEW-2026",
    ],
    mappings: [
      {
        supplierCode: "PV14-8RB-3K",
        articleNumber: "702038",
        confidence: "known",
        candidates: [],
      },
      {
        supplierCode: "DNF14-250FIB-3K",
        articleNumber: "703118",
        confidence: "suggested",
        candidates: [
          { articleNumber: "703118", name: "FST 6,3 x 0,8 blau, vollisoliert" },
          { articleNumber: "703119", name: "FST 6,3 x 0,8 rot, vollisoliert" },
        ],
      },
      {
        supplierCode: "MICRO-PV14-8RY",
        articleNumber: null,
        confidence: "unresolved",
        candidates: [
          { articleNumber: "702039", name: "Ringöse 4-2,5 gelb (variant)" },
          { articleNumber: "702038", name: "Ringöse 4-2,5 blau" },
        ],
      },
      {
        supplierCode: "MICRO-XR-400",
        articleNumber: null,
        confidence: "unresolved",
        candidates: [],
      },
      {
        supplierCode: "MICRO-NEW-2026",
        articleNumber: null,
        confidence: "unresolved",
        candidates: [],
      },
    ] satisfies DeclarationMapping[],
  },
  sds: {
    label: "Safety Data Sheet",
    doc: "Santoprene_251-70W232_Sicherheitsdatenblatt.pdf",
    productName: "SANTOPRENE 251-70W232 LF3001 NAT (FOR)",
    productNumber: "000000000021053610",
    ufi: "75S5-N060-3009-K80S",
    pages: 15,
    revisedAt: "2024-10-24",
    manufacturer: "Celanese Sales Germany GmbH",
    substances: [
      {
        name: "Diantimontrioxid",
        cas: "1309-64-4",
        ec: "215-175-0",
        conc: ">= 1 - < 10% w/w",
        hazard: "Carc. 2 H351; STOT RE 2 H373",
      },
      {
        name: "Zinkoxid",
        cas: "1314-13-2",
        ec: "215-222-5",
        conc: ">= 0,25 - < 1% w/w",
        hazard: "Aquatic Acute 1 H400; Chronic 1 H410",
      },
      {
        name: "Zinndichlorid",
        cas: "7772-99-8",
        ec: "231-868-0",
        conc: ">= 0,1 - < 0,25% w/w",
        hazard: "Skin Corr. 1B H314; Eye Dam. 1 H318",
      },
    ],
  },
} as const

export type DeclarationScenario =
  (typeof DECLARATION_SCENARIOS)[keyof typeof DECLARATION_SCENARIOS]

// ─── Declaration detail: linked articles + audit ─────────────────────────────

export function useDeclarationArticles(declarationId: string) {
  return useQuery({
    queryKey: ["mc", "declarations", declarationId, "articles"],
    queryFn: async (): Promise<DeclarationLinkedArticle[]> => {
      // Backend returns a bare array (not paginated envelope).
      return apiClientV2
        .get(`/material-compliance/declarations/${declarationId}/articles`)
        .then((r) => r.data ?? [])
    },
    enabled: !!declarationId,
  })
}

export function useDeclarationAudit(declarationId: string) {
  return useQuery({
    queryKey: ["mc", "declarations", declarationId, "audit"],
    queryFn: async (): Promise<DeclarationAuditEvent[]> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declarationId}/audit`)
        .then((r) => r.data)
    },
    enabled: !!declarationId,
  })
}

/**
 * The orion R2 read-model — the batched source the three-state `cellState`
 * classifier derives from. Fetched WITHOUT the snake→camel case transform
 * (`skipCaseConversion`): the payload carries meaningful data in its object keys
 * (`framework:articleNumber` / `framework:groupIndex`), which the transform would
 * corrupt, and cellState reads the pipeline's snake sub-fields verbatim.
 *
 * `refetchOnWindowFocus` lets a caller outside the review page opt back in to the
 * refocus refetch `REVIEW_READ_OPTIONS` turns off (react-query decides it per
 * observer).
 */
export function useDeclarationReadModel(
  declarationId: string,
  options: { refetchOnWindowFocus?: boolean; enabled?: boolean } = {},
) {
  const { enabled = true, ...rest } = options
  return useQuery({
    queryKey: ["mc", "declarations", declarationId, "read-model"],
    queryFn: async (): Promise<DeclarationReadModel> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declarationId}/read-model`, {
          skipCaseConversion: true,
        } as AxiosRequestConfig & { skipCaseConversion?: boolean })
        .then((r) => r.data)
    },
    enabled: enabled && !!declarationId,
    ...REVIEW_READ_OPTIONS,
    ...rest,
  })
}

// ─── Article search ───────────────────────────────────────────────────────────

export function useSearchArticles(
  declId: string,
  q: string,
  supplierId?: string,
) {
  return useQuery({
    queryKey: ["mc", "articles", "search", declId, q, supplierId],
    queryFn: async (): Promise<Article[]> => {
      const params = new URLSearchParams({ q: q })
      if (supplierId) params.set("supplier_id", supplierId)
      return apiClientV2
        .get(`/material-compliance/master-data/articles?${params.toString()}`)
        .then((r) => r.data.items)
    },
    enabled: q.length >= 2,
  })
}

// ─── Review flow hooks ────────────────────────────────────────────────────────

/** Built, never inlined (see `conditionFieldsKey`'s note on the source guard). */
function openItemsKey(declarationId: string) {
  return ["mc", "declarations", declarationId, "open-items"]
}

/**
 * Refetch the server's open items (C1-d) so the answer always reflects the latest
 * write. React Query's `invalidateQueries` cancels and restarts a fetch only when
 * the query already HOLDS data; during the FIRST load it joins the request already
 * in flight, so an answer computed before the gesture lands as "fresh" and nothing
 * refetches. That first load is cancelled here and started again instead.
 */
export function refreshOpenItems(
  queryClient: QueryClient,
  declarationId: string,
) {
  const key = openItemsKey(declarationId)
  const state = queryClient.getQueryState(key)
  if (state && state.data === undefined && state.fetchStatus === "fetching") {
    void queryClient
      .cancelQueries({ queryKey: key, exact: true })
      .then(() => queryClient.invalidateQueries({ queryKey: key, exact: true }))
    return
  }
  void queryClient.invalidateQueries({ queryKey: key, exact: true })
}

/**
 * EVERY REVIEW READ THAT COMES OUT OF THE PERSISTED INTERFACE — the reads that are
 * empty for the whole of an extraction and land, all at once, when the run's interface
 * is written. Named here, beside the hooks that define the keys, so the list cannot
 * drift from the queries it stands for.
 *
 * `document` and `audit` are deliberately absent: the source PDF does not change with
 * the run, and re-fetching a blob costs a download for nothing.
 */
const INTERFACE_READS = [
  "extraction",
  "statements",
  "scope",
  "read-model",
  "article-states",
  "condition-fields",
  "articles",
  "statement-articles",
  "framework",
  "memory-hints",
  "open-items",
  // Asked vs delivered (TUR-1732): read before the statements existed, every asked
  // regulation shows missing until the run lands.
  "inquiry-coverage",
] as const

/**
 * HYDRATE THE FLOW IN PLACE once a declaration's extraction has finished.
 *
 * A reviewer can now open the review route while the declaration is still processing,
 * so every read above was fetched mid-run and is empty. This marks them stale and
 * refetches them — no remount, so a guided position, a scroll offset and the URL
 * contract all survive the moment the run lands.
 *
 * WHAT TO FIRE IT ON, and why it is NOT the run feed's `run_completed`. The pipeline
 * emits that bookend from the orchestrator, in its own transaction, and only THEN
 * persists the interface, stages, and sets `processing_status = 'processed'` — one
 * commit, at the end. So `run_completed` is visible while the interface still is not,
 * and a refetch armed on it reads the pre-interface state the caller must never be
 * left with. Worse, the event is replayed to every client that starts its cursor at 0,
 * which is every fresh mount, so an already-settled declaration would re-fetch its
 * whole flow on arrival. The declaration's own lifecycle leaving `processing` is the
 * signal that is ATOMIC with the interface write — it cannot be early, and it cannot
 * fire twice.
 */
export function useHydrateDeclarationInterface(declId: string) {
  const queryClient = useQueryClient()
  return useCallback(() => {
    for (const read of INTERFACE_READS) {
      if (read === "open-items") refreshOpenItems(queryClient, declId)
      else
        queryClient.invalidateQueries({
          queryKey: ["mc", "declarations", declId, read],
        })
    }
    invalidateDeclarationExemptions(queryClient, declId)
  }, [queryClient, declId])
}

/**
 * Refresh EVERYTHING this declaration is read through, by prefix.
 *
 * WHY A PREFIX AND NOT A LIST. Every review gesture used to name the reads it thought it moved, and
 * the review screen reads more than any one of them named — `statements`, `scope`, `condition-fields`,
 * `read-model` and `article-states` all feed the same cells. So a gesture could persist server-side
 * and leave the screen showing the state it just changed, which reads as a frozen product rather than
 * an error. Two reported symptoms were both this:
 *
 *   - "ignore saves but the screen never updates" — `useRecordOverride` refreshed `statements` and
 *     `read-model`, while the cell's verify-state is derived from `condition-fields`.
 *   - "taking the article out of scope just asks for the articles back" —
 *     `useUpdateStatementScope` refreshed `scope` and `statements`, while the issue that demanded the
 *     articles is computed in `read-model`.
 *
 * Four gestures invalidated nothing at all. Enumerating reads per mutation is a list that drifts every
 * time a screen composes one more hook, and the failure is silent — so it is not enumerated any more.
 *
 * Every declaration read is keyed `["mc", "declarations", <id>, …]`, and React Query matches by
 * prefix, so this covers all of them including reads added later. The cost is one extra refetch of a
 * few small reads after a DELIBERATE user gesture, which is the right trade against a screen that
 * lies about what was saved.
 *
 * EXCEPT THE SOURCE DOCUMENT. `useDeclarationDocument` is keyed under the same prefix but fetches
 * the whole supplier PDF as an ArrayBuffer, and `flowShared` mounts it on the review screen — so it
 * is an ACTIVE query, and `invalidateQueries` refetches active queries by default. Without this
 * exclusion every one of the nine gestures re-downloads the entire file, which is not "a few small
 * reads" and is what a reviewer does dozens of times per declaration.
 *
 * It is also the one read no gesture can move: the source PDF is immutable for a declaration. So
 * this is a genuine exception rather than a performance compromise — excluding it costs nothing and
 * cannot leave the screen stale. Keyed by position (`queryKey[3]`) so the prefix still covers reads
 * added later, which is the property that made the prefix worth having.
 */
export function invalidateDeclaration(
  queryClient: ReturnType<typeof useQueryClient>,
  declarationId: string,
  options: { cause?: "recompute" } = {},
) {
  queryClient.invalidateQueries({
    queryKey: ["mc", "declarations", declarationId],
    predicate: (query) =>
      query.queryKey[3] !== "document" &&
      query.queryKey[3] !== "open-items" &&
      // A recalculation settling never changes the supplier pool the paged
      // review's background `/scope` read holds (Step C3).
      !(options.cause === "recompute" && keepsPagedSupplierPool(query)),
  })
  // C1-d: its own path, so a gesture during the FIRST load is not lost.
  refreshOpenItems(queryClient, declarationId)
  // The register only (a DB read): a statement gesture can add a framework that has one.
  // Not the proposals: almost every gesture lands here, and each refresh is a model call.
  queryClient.invalidateQueries({
    queryKey: ["mc", "declaration-exemptions", declarationId, "entries"],
  })
}

/** Register and proposals both. The proposals depend on a statement's verdict, scope and
 *  disclosures and on which exemptions are saved, so this runs once a cone has settled
 *  (`useHydrateDeclarationInterface`) and after a cleared exemption or a changed binding;
 *  never on every save, each refresh being a model call. */
export function invalidateDeclarationExemptions(
  queryClient: QueryClient,
  declarationId: string,
) {
  queryClient.invalidateQueries({
    queryKey: ["mc", "declaration-exemptions", declarationId],
  })
}

/** `invalidateDeclarationExemptions` once `save` lands. The rejection is absorbed: a failed
 *  save is already reported on its field, and nothing here should re-raise it. */
export function refreshExemptionsAfter(
  queryClient: QueryClient,
  declarationId: string,
  save: Promise<unknown>,
): Promise<void> {
  return save.then(
    () => invalidateDeclarationExemptions(queryClient, declarationId),
    () => undefined,
  )
}

// ─── Optimistic statement edits (TUR-1211, W5-5) ──────────────────────────────

function statementsQueryKey(declarationId: string) {
  return ["mc", "declarations", declarationId, "statements"] as const
}

/** The one exception to "every review mutation uses `invalidateDeclaration`"
 *  above, and deliberately not a hand-named key list of the kind that
 *  regression guard (`declarationsApi.test.tsx`, "no review mutation names
 *  individual reads any more") exists to catch: a verdict or scope switch is
 *  the highest-frequency gesture on the review screen (one click per
 *  statement, dozens per declaration), and it only ever moves FIVE reads:
 *  `statements` (what it wrote), `read-model` (the cell classifier),
 *  `article-states` (the verify matrix), `condition-fields` (the
 *  condition editor's own subscription) and `scope` (the DECLARATION-wide
 *  pool, `useDeclarationScope`): `update_statement_scope` grows that pool
 *  and sets `scope_mode` server-side, so a per-STATEMENT scope write DOES
 *  move the declaration-wide resource too (W5-5 review finding 7), and
 *  `useDeclarationScope` went stale without it. A verdict change never
 *  moves the pool, but this list is one pinned contract shared by both
 *  mutations, kept simple rather than split per-mutation. `articles`,
 *  `audit` and `extraction` are read elsewhere on the app (article detail,
 *  the declaration panel's audit tab, the extraction viewer) but none of
 *  them renders anything a verdict/scope edit can change, checked against
 *  every consumer under `studio-v2/` and `DeclarationPanel.tsx` before
 *  writing this list (see the W5-5 report). Iterates an array rather than
 *  inlining a
 *  literal key per read, same shared helper for both mutations below, so
 *  this stays the one address the next reviewer edits, not a copy each
 *  mutation could drift from independently. */
const STATEMENT_CHANGE_READS = [
  "statements",
  "read-model",
  "article-states",
  "condition-fields",
  "scope",
  "statement-articles",
  "framework",
  "open-items",
] as const

/**
 * `statementId` (Step C3 paged review): a statement edit changes its OWN regulation's
 * slice only, so the other regulations' cached slices stay fresh instead of being
 * downloaded again on the next visit (megabytes each on a big declaration). Without
 * it, or when no cached slice holds that statement, every slice is refreshed, as
 * before. A recalculation the edit starts refreshes everything when it settles.
 */
export function invalidateAfterStatementChange(
  queryClient: ReturnType<typeof useQueryClient>,
  declarationId: string,
  statementId?: string,
) {
  const ownFramework = statementId
    ? frameworkOfCachedStatement(queryClient, declarationId, statementId)
    : null
  for (const read of STATEMENT_CHANGE_READS) {
    if (read === "open-items") refreshOpenItems(queryClient, declarationId)
    else if (read === "framework" && ownFramework)
      queryClient.invalidateQueries({
        queryKey: frameworkSliceKey(declarationId, ownFramework),
        exact: true,
      })
    else
      queryClient.invalidateQueries({
        queryKey: ["mc", "declarations", declarationId, read],
        ...(read === "scope" && {
          predicate: (query) => !keepsPagedSupplierPool(query),
        }),
      })
  }
}

/** The regulation whose cached slice holds `statementId`, or null. */
function frameworkOfCachedStatement(
  queryClient: QueryClient,
  declarationId: string,
  statementId: string,
): string | null {
  for (const [, slice] of queryClient.getQueriesData<DeclarationFrameworkSlice>(
    {
      queryKey: frameworkSlicePrefix(declarationId),
    },
  )) {
    if (slice?.statements?.groups?.some((g) => g.statementId === statementId))
      return slice.framework
  }
  return null
}

/** The paged review's background `/scope` read while it holds a SUPPLIER pool: a
 *  statement edit or a recalculation never changes the supplier's articles, so it is
 *  not downloaded again (megabytes on a big supplier). Without a supplier pool the
 *  candidates come from the declaration's own links, which such a write can change:
 *  then it is refreshed, as always. */
function keepsPagedSupplierPool(query: {
  meta?: Record<string, unknown>
  state: { data?: unknown }
}): boolean {
  if (query.meta?.pagedBackgroundScope !== true) return false
  const data = query.state.data as DeclarationScopeOut | null | undefined
  return (data?.articlePool?.length ?? 0) > 0
}

/** The one statement's patch a verdict or scope edit's `onMutate` applies:
 *  pure and exported so the "which fields move, which stand" rule is
 *  testable without a `QueryClient` or a mocked `apiClient`.
 *
 *  `undefined` on a patch field means "leave it standing" (mirrors
 *  `EditStatementVerdictIn.verdict`'s own null-means-standing contract at
 *  the wire), never "clear it": a verdict-only-null, listVersion-only edit
 *  (`EditionRow`) must not blank `verdictOverride`.
 *
 *  Returns the SAME array reference when the statement is not found (no
 *  declaration this editor is not showing should ever re-render from this),
 *  and `undefined` unchanged when the cache had nothing seeded yet (an
 *  unmounted/unobserved `statements` query, nothing to patch, nothing to
 *  roll back either). */
export function patchStatement(
  groups: StatementGroup[] | undefined,
  statementId: string,
  patch: {
    verdictOverride?: string
    listVersion?: string | null
    scopeAll?: boolean
    articleNumbers?: string[]
  },
  opts: { pending: boolean },
): StatementGroup[] | undefined {
  if (!groups) return groups
  let touched = false
  const next = groups.map((group) => {
    let groupTouched = false
    const nextEntries = group.groups.map((entry): StatementGroupEntry => {
      if (entry.statementId !== statementId) return entry
      touched = true
      groupTouched = true
      return {
        ...entry,
        ...(patch.verdictOverride !== undefined
          ? { verdictOverride: patch.verdictOverride }
          : {}),
        ...(patch.scopeAll !== undefined ? { scopeAll: patch.scopeAll } : {}),
        ...(patch.articleNumbers !== undefined
          ? { articleNumbers: patch.articleNumbers }
          : {}),
        values:
          patch.listVersion !== undefined
            ? { ...entry.values, listVersion: patch.listVersion }
            : entry.values,
        pending: opts.pending,
      }
    })
    return groupTouched ? { ...group, groups: nextEntries } : group
  })
  return touched ? next : groups
}

/** Restores ONE statement's row from a snapshot onto the CURRENT cache, rather
 *  than replacing the whole `statements` array with the snapshot. Two quick
 *  edits on different statements interleave through the same query key: if
 *  the first one's rollback swapped in its own snapshot wholesale, it would
 *  also revert whatever the second mutation had already patched or settled
 *  onto the SAME array. Restoring only the failed statement's entry, from
 *  the snapshot that mutation itself captured, leaves every other statement
 *  exactly as the current cache has it. Falls back to `current` unchanged
 *  when there is nothing to restore (no current cache, no snapshot, or the
 *  statement is not in the snapshot). */
export function restoreStatementFromSnapshot(
  current: StatementGroup[] | undefined,
  previous: StatementGroup[] | undefined,
  statementId: string,
): StatementGroup[] | undefined {
  if (!current || !previous) return current
  let restoredEntry: StatementGroupEntry | undefined
  for (const group of previous) {
    const found = group.groups.find(
      (entry) => entry.statementId === statementId,
    )
    if (found) {
      restoredEntry = found
      break
    }
  }
  if (!restoredEntry) return current
  let touched = false
  const next = current.map((group) => {
    let groupTouched = false
    const nextEntries = group.groups.map((entry) => {
      if (entry.statementId !== statementId) return entry
      touched = true
      groupTouched = true
      return restoredEntry as StatementGroupEntry
    })
    return groupTouched ? { ...group, groups: nextEntries } : group
  })
  return touched ? next : current
}

/** Per-statement mutation generation, so a slower mutation's `onError` can
 *  tell whether a NEWER mutation has started on the SAME statement since it
 *  snapshotted the cache. Two quick edits on one statement race the network:
 *  edit 1 snapshots and patches, edit 2 snapshots (edit 1's patch already
 *  applied) and patches again; if edit 1 then fails, its rollback must not
 *  fire once edit 2 has taken over that statement. `onSettled`'s
 *  `invalidateAfterStatementChange` is what reconciles the stale mutation's
 *  own failure with the server.
 *
 *  Keyed by `QueryClient` via `WeakMap` (not a bare module-level `Map`) so
 *  tests that each mount a fresh `QueryClient` stay isolated from each
 *  other's counters without any manual reset between them. */
const statementMutationGenerations = new WeakMap<
  ReturnType<typeof useQueryClient>,
  Map<string, number>
>()

function nextStatementGeneration(
  queryClient: ReturnType<typeof useQueryClient>,
  statementId: string,
): number {
  let generations = statementMutationGenerations.get(queryClient)
  if (!generations) {
    generations = new Map()
    statementMutationGenerations.set(queryClient, generations)
  }
  const generation = (generations.get(statementId) ?? 0) + 1
  generations.set(statementId, generation)
  return generation
}

function isLatestStatementGeneration(
  queryClient: ReturnType<typeof useQueryClient>,
  statementId: string,
  generation: number,
): boolean {
  return (
    statementMutationGenerations.get(queryClient)?.get(statementId) ===
    generation
  )
}

// ─── The orion review gestures (H2 / H3) ──────────────────────────────────────

/**
 * ENTER AN OVERRIDE — the Override bucket's note, at one address.
 *
 * THE COMMENT IS MANDATORY and the server refuses a blank one (422
 * `OVERRIDE_COMMENT_REQUIRED` — `mc_declaration_review_service.py:550`, pinned by
 * `backend/tests/mc/test_orion_review_endpoints.py:212`). There is no document quote
 * behind an override, so the note takes the quote's place as the record of why;
 * `OverrideControl` disables its own button on a blank, and the server's refusal is
 * the backstop rather than the only guard.
 *
 * The two-tab port deliberately did NOT relax this, though the studio's Phase B did
 * on its own side: the requirement is a product invariant recorded and tested here.
 * What the port DID add is `overrideRefusalKey`, which turns this endpoint's 422 into
 * the reader's own sentence — every refusal it can raise used to render as a generic
 * "could not record".
 *
 * A DISPOSITION, NEVER A VALUE. Nothing here writes a verdict, a scope or a cell —
 * every value keeps its existing writer, and membership stays derived. Both reads
 * that carry a note are invalidated: `statements` (the per-entry note and the
 * derived `proceed_unknown`) and `read-model` (the address-keyed map).
 */
export function useRecordOverride() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      address: string
      kind: string
      comment: string
    }): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/${input.declarationId}/overrides`,
        { address: input.address, kind: input.kind, comment: input.comment },
      )
    },
    onSuccess: (_data, v) => {
      invalidateDeclaration(queryClient, v.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/**
 * `Confirm edition` — ONE signal per framework, never one per statement: the run
 * processes exactly one edition of a reference list per document.
 *
 * PICKING IS CONFIRMING. There is no picked-but-unconfirmed state, so this fires a
 * cone and the caller arms the run poll on it. Confirming the edition the run
 * already selected costs nothing: every node finds its inputs unmoved and skips.
 */
export function useConfirmEdition() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      framework: string
      editionId: string
    }): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/${input.declarationId}/frameworks/${encodeURIComponent(input.framework)}/edition`,
        { editionId: input.editionId },
      )
    },
    onSuccess: (_data, v) => {
      // Every read this declaration is shown through. The rest of the cone's output
      // lands when the poll reports `recompute_settled`.
      invalidateDeclaration(queryClient, v.declarationId)
      announceRecompute(v.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/**
 * RUN THE DEFERRED FINAL REVIEW NOW — 202, and nobody waits on it.
 *
 * THE CLIENT IS THE TIMER, and it has to be: several workers behind a load balancer
 * means a server-side quiet timer would fire late, twice, or never, all
 * indistinguishable from working. The surface knows when it went quiet, so it asks;
 * no state is held between requests on either side.
 *
 * `started: false` is a NO-OP, not a failure — a declaration whose graph cannot be
 * rebuilt says so, and the caller does nothing rather than waiting on a run that
 * will never appear. See `useQuietReview`.
 */
export function useReviewNow() {
  const mutation = useMutation({
    // Told apart from a save by the approve footer's "saving" count.
    mutationKey: REVIEW_NOW_MUTATION_KEY,
    mutationFn: async (input: {
      declarationId: string
      /** Ask the server to answer `started: false` instead of starting the
       *  review while a recalculation is unsettled (the early start ahead of
       *  an approve). Absent: the request is exactly what it always was. */
      unlessRecomputing?: boolean
    }): Promise<{ declarationId: string; started: boolean }> => {
      const url = `/material-compliance/declarations/${input.declarationId}/review`
      const { data } = input.unlessRecomputing
        ? await apiClientV2.post(url, undefined, {
            params: { unless_recomputing: true },
          })
        : await apiClientV2.post(url)
      return data as { declarationId: string; started: boolean }
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export function useDeclarationExtraction(declId: string) {
  return useQuery({
    queryKey: ["mc", "declarations", declId, "extraction"],
    queryFn: async (): Promise<DeclarationExtractionOut | null> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declId}/extraction`)
        .then((r) => r.data)
    },
    enabled: !!declId,
    ...REVIEW_READ_OPTIONS,
  })
}

/** `enabled: false` (Step C3 paged review) keeps the full read off the wire: the
 *  page reads one regulation's slice instead. Default on, today's behaviour. */
export function useDeclarationStatements(
  declId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  return useQuery({
    queryKey: ["mc", "declarations", declId, "statements"],
    queryFn: async (): Promise<StatementGroup[]> => {
      // Backend returns interface.statement_groups verbatim (a bare array).
      return apiClientV2
        .get(`/material-compliance/declarations/${declId}/statements`)
        .then((r) => r.data ?? [])
    },
    enabled: enabled && !!declId,
    ...REVIEW_READ_OPTIONS,
  })
}

/** Marks the paged review's background `/scope` read (Step C3): the supplier pool
 *  with ids and names, which neither a recalculation nor a statement edit changes,
 *  so those refreshes leave it alone while it holds a supplier pool
 *  (`keepsPagedSupplierPool`). Structural gestures and hydration still refresh it. */
const PAGED_SCOPE_META = { pagedBackgroundScope: true } as const

/** `enabled: false` (Step C3 paged review) keeps the full read off the wire: the
 *  page reads one regulation's slice instead. Default on, today's behaviour.
 *  `paged`: the paged review's background read (30 s fresh, not refreshed by a
 *  recalculation settling — see `PAGED_SCOPE_META`). */
export function useDeclarationScope(
  declId: string,
  options: { enabled?: boolean; paged?: boolean } = {},
) {
  const { enabled = true, paged = false } = options
  return useQuery({
    queryKey: ["mc", "declarations", declId, "scope"],
    queryFn: async (): Promise<DeclarationScopeOut | null> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declId}/scope`)
        .then((r) => r.data)
    },
    enabled: enabled && !!declId,
    ...(paged && { staleTime: 30_000, meta: PAGED_SCOPE_META }),
    ...REVIEW_READ_OPTIONS,
  })
}

/** `enabled: false` (Step C3 paged review) keeps the full read off the wire: the
 *  page reads one regulation's slice instead. Default on, today's behaviour. */
export function useDeclarationArticleStates(
  declId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  return useQuery({
    queryKey: ["mc", "declarations", declId, "article-states"],
    queryFn: async (): Promise<DeclarationArticleStates | null> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declId}/article-states`)
        .then((r) => r.data)
    },
    enabled: enabled && !!declId,
    ...REVIEW_READ_OPTIONS,
  })
}

/** `enabled: false` (Step C3 paged review) keeps the full read off the wire: the
 *  page reads one regulation's slice instead. Default on, today's behaviour. */
export function useDeclarationConditionFields(
  declId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  return useQuery({
    queryKey: ["mc", "declarations", declId, "condition-fields"],
    queryFn: async (): Promise<DeclarationConditionFields | null> => {
      return apiClientV2
        .get(`/material-compliance/declarations/${declId}/condition-fields`)
        .then((r) => r.data)
    },
    enabled: enabled && !!declId,
    ...REVIEW_READ_OPTIONS,
  })
}

export interface ExemptionEntry {
  framework: string
  entryNumber: string
  substanceScope: string | null
  application: string | null
  maxConcentration: number | string | null
  expiryDate: string | null
  /** "active" | "renewal_pending" | "expired"; an unknown status reads as active. */
  status: string
}

export interface ExemptionSuggestion {
  framework: string
  statementId: string
  substanceCas: string
  entryNumber: string
  /** "document": the PDF names the code; "model": proposed from substance and material. */
  source: string
}

export interface DeclarationExemptions {
  entries: ExemptionEntry[]
  suggestions: ExemptionSuggestion[]
}

const NO_EXEMPTIONS: DeclarationExemptions = { entries: [], suggestions: [] }

async function fetchExemptions(
  declId: string,
  suggestions: boolean,
): Promise<DeclarationExemptions> {
  const r = await apiClientV2.get<Partial<DeclarationExemptions>>(
    `/material-compliance/declarations/${declId}/exemptions`,
    suggestions ? undefined : { params: { suggestions: false } },
  )
  return {
    entries: r.data?.entries ?? [],
    suggestions: r.data?.suggestions ?? [],
  }
}

/** The exemption register for the declaration's frameworks, and the entry proposed per
 *  statement and substance. Two reads: the register is a plain DB read and renders the
 *  picker at once; the proposals cost a model call and arrive when it answers. Both are
 *  keyed OUTSIDE the declaration's prefix, so a field save (`invalidateDeclaration` is not
 *  among its paths) never repeats the model call. */
export function useDeclarationExemptions(
  declId: string,
): DeclarationExemptions {
  const register = useQuery({
    queryKey: ["mc", "declaration-exemptions", declId, "entries"],
    queryFn: () => fetchExemptions(declId, false),
    enabled: !!declId,
    staleTime: Infinity,
    retry: false,
  })
  const proposals = useQuery({
    queryKey: ["mc", "declaration-exemptions", declId, "suggestions"],
    queryFn: () => fetchExemptions(declId, true),
    enabled: !!declId,
    staleTime: Infinity,
    retry: false,
  })
  // The proposals read carries the register too: it wins while the register read is
  // still empty (no register framework on the declaration when it ran).
  const entries = register.data?.entries.length
    ? register.data.entries
    : (proposals.data?.entries ?? NO_EXEMPTIONS.entries)
  return {
    entries,
    suggestions: proposals.data?.suggestions ?? NO_EXEMPTIONS.suggestions,
  }
}

function concentrationCellKey(r: {
  articleNumber: string
  substanceCas: string | null
}): string {
  return `${r.articleNumber}|${normalizeCas(r.substanceCas) ?? "-"}`
}

/** The condition-fields cache entry a successful save leaves behind.
 *
 * PURE, and exported, so the one rule that matters here — which memory hints survive a
 * save — is testable without a QueryClient, a permission guard, or a mocked axios call.
 *
 * TURNUS MEMORY'S HINTS SURVIVE THIS WRITE, minus whichever cell's VALUE ACTUALLY MOVED.
 * `rows` is the WHOLE wholesale-replace payload (`commitMany`'s `merged`) — every
 * concentration cell ever saved on this declaration, not only the one just edited — so
 * filtering `touched` from `rows` alone (as this once did) treated EVERY populated
 * concentration cell as touched on EVERY save, including one for an unrelated exemption
 * field: the whole point of this function, undone by its own input shape. `touched` is
 * now the symmetric difference between `previous.rows` and `rows` at the concentration
 * addresses — a cell whose value is unchanged (present, absent, or identical in both)
 * is not touched, and its hint survives; one that was added, removed, or changed is, and
 * its hint — a taken hint's own cell chief among them — is dropped correctly.
 */
export function mergeConditionFieldsAfterSave(
  previous: DeclarationConditionFields | undefined,
  declarationId: string,
  rows: ConditionFieldRow[],
): DeclarationConditionFields & { memory: ConcentrationMemoryHint[] } {
  const isConcentration = (r: ConditionFieldRow) =>
    r.fieldKey === "concentration"
  const before = new Map(
    (previous?.rows ?? [])
      .filter(isConcentration)
      .map((r) => [concentrationCellKey(r), r.value]),
  )
  const after = new Map(
    rows.filter(isConcentration).map((r) => [concentrationCellKey(r), r.value]),
  )
  const touched = new Set(
    [...new Set([...before.keys(), ...after.keys()])].filter(
      (k) => before.get(k) !== after.get(k),
    ),
  )
  return {
    declarationId,
    rows,
    memory: (previous?.memory ?? []).filter(
      (h) => !touched.has(concentrationCellKey(h)),
    ),
  }
}

const isSignOffRow = (r: ConditionFieldRow) => r.fieldKey === "__verified__"

/** The condition-fields cache write shared by every mutation that answers with the
 *  rows now persisted (the values PUT, POST verify): THE RESPONSE IS THE STATE.
 *  The server may have dropped a moved cell's sign-off (`drop_unverifiable_marks`)
 *  or added the marks a verify derived — a client that kept its own request as the
 *  new state would show green the server no longer holds. Routed through
 *  `mergeConditionFieldsAfterSave` so both callers keep the same TURNUS MEMORY
 *  hint-survival rule. */
/** Built, never inlined as a `queryKey: [...]` literal — this file's own regression
 *  guard ("no review mutation names individual reads any more", TUR-1211 W5-5)
 *  scans the source for exactly that shape. */
function conditionFieldsKey(declarationId: string) {
  return ["mc", "declarations", declarationId, "condition-fields"]
}

function cacheConditionRowsFromServer(
  queryClient: QueryClient,
  declarationId: string,
  rows: ConditionFieldRow[],
  seq: number | null = null,
) {
  const key = conditionFieldsKey(declarationId)
  const previous = queryClient.getQueryData<DeclarationConditionFields>(key)
  queryClient.setQueryData(
    key,
    mergeConditionFieldsAfterSave(previous, declarationId, rows),
  )
  stopSliceReads(queryClient, declarationId)
  cacheConditionRowsInSlices(queryClient, declarationId, rows, seq)
  // C1-d: a condition-row write moves the server's open items too (a filled
  // exemption number heals a cell; a framework-less row reaches every
  // regulation), and none of these writers invalidates the declaration prefix.
  refreshOpenItems(queryClient, declarationId)
}

/**
 * PUT /{id}/condition-fields — BYTE-FOR-BYTE origin/main. No screen calls it any
 * more (its only caller was the retired Studio V1); it is removed together with the
 * wholesale PUT route it wraps. No options, no body key Studio V2 invented: a
 * `keepMarks`-style flag here would reach an OLDER BACKEND that does not know it
 * (pydantic `extra='ignore'`) and silently wholesale-replace the table, deleting
 * every sign-off the moment a new frontend deployed ahead of its backend
 * (TUR-0000, sandbox deploy-skew incident). Studio V2's own values-only save is a
 * SEPARATE route (`useSaveConditionValues`, below) for exactly that reason: an
 * older backend answers 404 to it, loudly, instead of silently accepting a payload
 * it will misread.
 */
export function useSaveConditionFields() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (
      input: SaveConditionFieldsIn & { confirm?: boolean },
    ): Promise<void> => {
      // Wholesale replace. Deliberately does NOT refetch the condition-fields
      // query — the reviewer's live Screen C state is authoritative during the
      // session; the next mount refetches the persisted rows.
      //
      // SAVE IS NOT CONFIRM, and this one endpoint carries both: the payload is the
      // same wholesale state either way, and only the client knows whether the human
      // just typed something or pressed `Verify article`. `confirm` is that fact, and
      // the server fires the gesture's cone on it — a blur must never start work.
      await apiClientV2.put(
        `/material-compliance/declarations/${input.declarationId}/condition-fields`,
        { rows: input.rows, confirm: input.confirm ?? false },
      )
    },
    onSuccess: (_data, input) => {
      // The saved rows ARE the session's truth, so the cache is set to them rather
      // than refetched. The flow derivation (useDeclarationFlowState → the rail's
      // worklist) reads THIS query, not Screen C's local mirror — without the write
      // the rail kept classifying against the stale rows and a fully-verified issue
      // never turned ready until a remount, which read as "I have to leave and
      // come back before Continue appears".
      cacheConditionRowsFromServer(queryClient, input.declarationId, input.rows)
      // Only `Verify article` fires a cone; a plain save starts nothing.
      if (input.confirm) announceRecompute(input.declarationId)
    },
    // A REJECTED PUT MUST NEVER LOOK SAVED. The per-article grid's own
    // `commit()` used to swallow this (`settled.catch(() => {})`) so the
    // field kept showing the typed value with no cache write behind it, see
    // that component's own doc for the revert half of this fix. This half is
    // the toast: `toastApiError` skips >=500 (the global interceptor already
    // toasted it), everything else names what failed.
    onError: (error) => {
      toastApiError(error, translate, "MC_DECL_V2_COND_SAVE_FAILED")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

// Identifies the MUTATION TYPE for `queryClient.isMutating` filters below — not
// per-declaration (a zero-argument hook's `mutationKey` is fixed at definition
// time, before any call's `declarationId` exists); the per-declaration scoping is
// the `predicate` each filter carries alongside it, over `mutation.state.variables
// .declarationId`, which is the only place a per-call value can be read back.
/** How long a condition save refused with REVIEW_BUSY waits before its one retry:
 *  the server already waited its own bound for the row, so this is the extra time an
 *  approval in progress gets to commit (TUR-1725). Exported for the tests. */
export const REVIEW_BUSY_RETRY_DELAY_MS = 3_000

const SAVE_CONDITION_VALUES_MUTATION_KEY = [
  "mc",
  "declarations",
  "condition-fields",
  "values-save",
]

/** One changed row's address in a values-save (the PATCH's unit of write). */
function rowAddress(r: ConditionFieldRow): string {
  return `row|${r.articleNumber}|${normalizeCas(r.substanceCas) ?? "-"}|${r.framework ?? "-"}|${r.fieldKey}`
}

/** One reviewer binding's address in a values-save. */
function bindingAddress(b: {
  framework: string
  statementId: string
  substanceCas: string
}): string {
  return `binding|${b.framework}|${b.statementId}|${normalizeCas(b.substanceCas) ?? "-"}`
}

/** What one values-save writes, as addresses: `null` means the whole table (the
 *  PUT, `rows` without `changedRows`/`bindings`), which every other save touches. */
function valuesSaveAddresses(input: ValuesSaveInput): Set<string> | null {
  if (!input.changedRows && !input.bindings) return null
  const out = new Set<string>()
  for (const r of input.changedRows ?? []) out.add(rowAddress(r))
  for (const b of input.bindings ?? []) out.add(bindingAddress(b))
  return out
}

/** The addresses of `input` that a values-save for the same declaration, started
 *  AFTER `input`'s, also writes (TUR-1725), or `"all"` when either side is the
 *  whole-table PUT. `input`'s REVIEW_BUSY retry must not resend those: the older
 *  value would land after the newer one and overwrite it. Every OTHER address of
 *  `input` was written by nobody since, so its retry is still owed. Read off the
 *  mutation cache: `mutationId` grows with every `mutate`, and the cache entry
 *  whose `state.variables` is this very `input` object is this call's own. */
function addressesTakenByNewerSaves(
  queryClient: QueryClient,
  input: ValuesSaveInput,
): Set<string> | "all" {
  const saves = queryClient
    .getMutationCache()
    .findAll({ mutationKey: SAVE_CONDITION_VALUES_MUTATION_KEY })
  const own = saves.find((m) => m.state.variables === input)
  const taken = new Set<string>()
  if (!own) return taken
  const mine = valuesSaveAddresses(input)
  for (const m of saves) {
    if (m.mutationId <= own.mutationId) continue
    const other = m.state.variables as ValuesSaveInput | undefined
    if (!other || other.declarationId !== input.declarationId) continue
    const theirs = valuesSaveAddresses(other)
    if (mine === null || theirs === null) return "all"
    for (const a of theirs) if (mine.has(a)) taken.add(a)
  }
  return taken
}

/** `input` without the rows and bindings a newer save took over, or `null` when
 *  nothing of it is left to retry. */
function withoutTakenAddresses(
  input: ValuesSaveInput,
  taken: Set<string> | "all",
): ValuesSaveInput | null {
  if (taken === "all") return null
  if (taken.size === 0) return input
  const changedRows = input.changedRows?.filter(
    (r) => !taken.has(rowAddress(r)),
  )
  const bindings = input.bindings?.filter((b) => !taken.has(bindingAddress(b)))
  if (!changedRows?.length && !bindings?.length) return null
  return { ...input, changedRows, bindings }
}

type ValuesSaveInput = SaveConditionFieldsIn & {
  confirm?: boolean
  /** Only the rows this edit changed. Given: PATCH, and the server merges them
   *  into the STORED table (a value typed on a 10k-article statement no longer
   *  ships the whole table and no longer hits the PUT's 5,000-row cap). Absent:
   *  the PUT with `rows` as the whole table, as before. */
  changedRows?: readonly ConditionFieldRow[]
  /** Reviewer bindings as commands (PATCH too): this statement's links for this
   *  substance become exactly `articleNumbers`; the server applies them to its
   *  stored rows, so a binding never ships the whole table either. */
  bindings?: readonly {
    framework: string
    statementId: string
    substanceCas: string
    articleNumbers: readonly string[]
  }[]
  /** Paged review only (C3-b1): the PATCH answers with what changed
   *  (`ConditionFieldsEcho`) instead of the whole table, and that is written
   *  into the cached regulation slices. Never sent with the flag off: the
   *  full-table caches below need the whole table. */
  echo?: boolean
}

/** One values-save request: the PATCH (`changedRows`/`bindings`) or the PUT. */
async function sendValuesSave(
  input: ValuesSaveInput,
): Promise<ConditionFieldRow[] | ConditionFieldsEcho> {
  const url = `/material-compliance/declarations/${input.declarationId}/condition-fields/values`
  if (input.changedRows || input.bindings) {
    const body = {
      rows: (input.changedRows ?? []).filter((r) => !isSignOffRow(r)),
      bindings: input.bindings ?? [],
      confirm: input.confirm ?? false,
    }
    if (input.echo) {
      const { data } = await apiClientV2.patch<ConditionFieldsEcho>(url, body, {
        params: { echo: "changed" },
      })
      return {
        echo: "changed",
        rows: data?.rows ?? [],
        removed: data?.removed ?? [],
        saveSeq: data?.saveSeq ?? null,
      }
    }
    const { data } = await apiClientV2.patch<ConditionFieldRowsOut>(url, body)
    return data?.rows ?? []
  }
  const { data } = await apiClientV2.put<ConditionFieldRowsOut>(url, {
    rows: input.rows.filter((r) => !isSignOffRow(r)),
    confirm: input.confirm ?? false,
  })
  return data?.rows ?? []
}

/** REVIEW_BUSY errors whose retry was skipped because a newer save of the same
 *  field took over: that newer save reports the field's fate, so no toast. */
const supersededBusySaves = new WeakSet<object>()

/** Per values-save input (the very object passed to `mutate`): the addresses its
 *  REVIEW_BUSY retry left out because a newer save wrote them (TUR-1725). */
const supersededSaveAddresses = new WeakMap<object, Set<string> | "all">()

/** Whether `error` is a values-save's REVIEW_BUSY whose one retry was skipped because
 *  a newer save writing the same address (or the whole table) exists (TUR-1725). The
 *  field must not offer "Save again" for it: sending the older value now is exactly
 *  what the skipped retry avoided (`useConditionFieldWrites`). */
export function isSupersededValuesSave(error: unknown): boolean {
  return !!error && typeof error === "object" && supersededBusySaves.has(error)
}

/** Whether `row` of the values-save `input` (the object given to `mutate`) was left
 *  out of its REVIEW_BUSY retry because a newer save wrote the same address
 *  (TUR-1725). The save's other rows were retried with their own values; this one's
 *  value is the newer save's, so the field showing it re-seeds from server truth. */
export function isRowSupersededInValuesSave(
  input: object,
  row: ConditionFieldRow,
): boolean {
  const taken = supersededSaveAddresses.get(input)
  return taken === "all" || (taken?.has(rowAddress(row)) ?? false)
}
const VERIFY_STATEMENT_MUTATION_KEY = [
  "mc",
  "declarations",
  "condition-fields",
  "verify",
]

/**
 * PUT /{id}/condition-fields/values — Studio V2's own save: VALUES ONLY. A route
 * an older backend has never heard of (404, loud) rather than a flag on the
 * wholesale PUT an older backend would silently misread (see that hook's own
 * doc). Strips any `__verified__` row from the wire — Studio V2 never signs
 * through a save, `useVerifyStatement` does — and caches the RESPONSE rows,
 * never the request: the server may drop a moved cell's mark
 * (`drop_unverifiable_marks`) or carry a stored one forward, and only the
 * response says which.
 */
export function useSaveConditionValues() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationKey: SAVE_CONDITION_VALUES_MUTATION_KEY,
    mutationFn: async (
      input: ValuesSaveInput,
    ): Promise<ConditionFieldRow[] | ConditionFieldsEcho> => {
      // REVIEW_BUSY (TUR-1725): the declaration row stayed held past the save's
      // bounded wait (an approval in its decisive part, another save, a
      // recalculation), and NOTHING was written, so sending the same rows again is
      // safe. Once, after the holder had time to settle, and only for the addresses
      // no NEWER save writes: that one carries the reviewer's latest value there,
      // and this retry landing after it would overwrite it with an older one. A
      // second refusal (or a fully skipped retry) reaches the field, which keeps the
      // typed value and offers "Save again" (`useConditionFieldWrites`).
      try {
        return await sendValuesSave(input)
      } catch (error) {
        if (getApiErrorCode(error) !== "REVIEW_BUSY") throw error
        await new Promise((resolve) =>
          setTimeout(resolve, REVIEW_BUSY_RETRY_DELAY_MS),
        )
        // Per address: a newer save of SOME of this save's rows takes over only
        // those; the rest is retried with its own values (never the taken ones).
        const taken = addressesTakenByNewerSaves(queryClient, input)
        if (taken === "all" || taken.size > 0)
          supersededSaveAddresses.set(input, taken)
        const retry = withoutTakenAddresses(input, taken)
        if (retry === null) {
          if (error && typeof error === "object") supersededBusySaves.add(error)
          throw error
        }
        return await sendValuesSave(retry)
      }
    },
    onSuccess: (result, input) => {
      if (Array.isArray(result))
        cacheConditionRowsFromServer(queryClient, input.declarationId, result)
      else {
        stopSliceReads(queryClient, input.declarationId)
        applyConditionEcho(queryClient, input.declarationId, result)
        // The whole table is not in hand on this path: mark it stale for whoever
        // reads it next, without refetching ~20k rows now.
        void queryClient.invalidateQueries({
          queryKey: conditionFieldsKey(input.declarationId),
          exact: true,
          refetchType: "none",
        })
        // The echo moves the server's open items exactly as a full-table write does.
        refreshOpenItems(queryClient, input.declarationId)
      }
      // A reviewer binding DOES change a substance's article list (the paged table
      // pages that list), even at the same count: refetch it, or a row would keep
      // pointing at an article the substance is no longer bound to.
      if (input.bindings?.length)
        void queryClient.invalidateQueries({
          queryKey: statementArticlesPrefix(input.declarationId),
        })
      // Only a confirming save fires a cone; a plain save starts nothing.
      if (input.confirm) announceRecompute(input.declarationId)
    },
    onError: (error) => {
      // A busy save newer saves took over entirely (its retry skipped,
      // `addressesTakenByNewerSaves`): the newer ones report the fields' fate.
      if (isSupersededValuesSave(error)) return
      toastApiError(error, translate, "MC_DECL_V2_COND_SAVE_FAILED")
    },
    onSettled: (_rows, _error, input) => {
      // TWO overlapping values-saves for the SAME declaration can have their
      // RESPONSES arrive out of order — `onSuccess` above then leaves the
      // EARLIER request's now-stale rows as the cache's last write, because it
      // trusts whichever response happens to land last, not whichever request
      // was sent last. `isMutating` still counts THIS call while `onSettled`
      // runs, so `=== 1` means no sibling values-save for this declaration is
      // still in flight — this is the LAST one to settle, and only it
      // re-syncs the cache from the server instead of trusting any one
      // response's rows.
      const stillSaving = queryClient.isMutating({
        mutationKey: SAVE_CONDITION_VALUES_MUTATION_KEY,
        // `variables` is typed `unknown` across mutation keys; this key's are the
        // values-save input, which always carries `declarationId`.
        predicate: (m) =>
          (m.state.variables as { declarationId?: string } | undefined)
            ?.declarationId === input.declarationId,
      })
      // Paged (`echo`): each echo is applied per address in save order
      // (`applyConditionEcho`), and the slices are refetched once if any write
      // came out of order (`settleConditionWrite`, below).
      if (stillSaving === 1 && !input.echo) {
        queryClient.invalidateQueries({
          queryKey: conditionFieldsKey(input.declarationId),
          exact: true,
        })
        // The paged article tables (C4) are NOT refetched here: a values-save
        // changes no article's name or order, and the table reads its values
        // from the condition rows above. Refetching every loaded page per cell
        // would cost up to 20 requests on a scrolled 10k substance. A binding,
        // which does change the list, refetches them in `onSuccess`.
      }
      settleConditionWrite(queryClient, input.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/** One article of a statement's page (C4, `GET /{id}/statements/{sid}/articles`).
 *  The endpoint also returns per-row values and a per-field summary; the paged
 *  table reads values from the condition rows instead, so they are not typed here. */
export interface StatementArticleRow {
  articleNumber: string
  name: string | null
}

export interface StatementArticlesPage {
  total: number
  /** The substance's (or statement's) whole list before `q`: the paged table
   *  checks it against the list the editor holds and falls back on a mismatch. */
  totalUnfiltered: number
  items: StatementArticleRow[]
  nextCursor: string | null
}

export interface StatementArticlesParams {
  /** A substance's persisted CAS; `""` for the nameless slot. Absent: the
   *  statement's whole article list. */
  substance?: string
  q?: string
  sort?: "scope" | "number" | "name"
  limit?: number
  enabled?: boolean
}

function statementArticlesPrefix(declId: string) {
  return ["mc", "declarations", declId, "statement-articles"] as const
}

/** The exact key of one `useStatementArticles` list (the same params it was called
 *  with, `enabled` aside), so a caller can reset that one list and no other. */
export function statementArticlesKey(
  declId: string,
  statementId: string,
  query: Omit<StatementArticlesParams, "enabled"> = {},
) {
  return [...statementArticlesPrefix(declId), statementId, query] as const
}

/**
 * THE SERVER'S OPEN ITEMS (C1-d): `GET /{id}/open-items`, the same derivation approve
 * gates on, with the per-regulation summary (C3-s). Read only in paged mode
 * (`useReviewPaged`); flag off never sends it.
 *
 * `skipCaseConversion`: `by_framework` is keyed by regulation slug, which the
 * client's camel-casing would rewrite (`eu_pop` → `euPop`); the screen converts the
 * fields it reads itself (`serverWorklist.ts`). Under the declaration prefix, so every
 * gesture's `invalidateDeclaration` refetches it; `staleTime: 0` because it is the
 * list Finish waits on. A 4xx is final (an older backend, a missing declaration).
 */
export function useDeclarationOpenItems(
  declId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: openItemsKey(declId),
    queryFn: () =>
      apiClientV2
        .get<DeclarationOpenItemsWire>(
          `/material-compliance/declarations/${declId}/open-items`,
          { skipCaseConversion: true } as AxiosRequestConfig & {
            skipCaseConversion?: boolean
          },
        )
        .then((r) => r.data),
    enabled: enabled && !!declId,
    staleTime: 0,
    ...REVIEW_READ_OPTIONS,
    retry: retryUnless4xx,
  })
}
/**
 * GET /{id}/statements/{statementId}/articles — Step C4: a statement's articles one
 * page at a time (today's list, today's order), optionally for one substance with its
 * values and a per-field summary over all of its articles. Keyed under the
 * declaration's prefix, so every gesture's prefix invalidation refreshes it too.
 */
export function useStatementArticles(
  declId: string,
  statementId: string,
  params: StatementArticlesParams = {},
) {
  const { enabled = true, ...query } = params
  return useInfiniteQuery({
    queryKey: [...statementArticlesPrefix(declId), statementId, query],
    queryFn: ({ pageParam }) =>
      apiClientV2
        .get<StatementArticlesPage>(
          `/material-compliance/declarations/${declId}/statements/${statementId}/articles`,
          {
            params: {
              substance: query.substance,
              q: query.q || undefined,
              sort: query.sort,
              limit: query.limit,
              cursor: pageParam ?? undefined,
            },
            paramsSerializer: { indexes: null },
          },
        )
        .then((r) => ({
          items: r.data?.items ?? [],
          total: r.data?.total ?? 0,
          totalUnfiltered: r.data?.totalUnfiltered ?? 0,
          nextCursor: r.data?.nextCursor ?? null,
        })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && !!declId && !!statementId,
    // A page's names and order change only with the statement's article set,
    // which every such gesture invalidates explicitly; a window focus must not
    // refetch every loaded page of a scrolled 10k table.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // A 4xx (an older backend without the route, a rejected cursor) is final:
    // the table falls back or restarts at once instead of after the retries.
    retry: (failureCount, error) => {
      const status = getApiErrorStatus(error)
      if (status !== null && status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })
}

// ─── One regulation at a time (Step C3) ──────────────────────────────────────

/** GET /{id}/frameworks/{fw} — one regulation's review reads (C3), in the client's
 *  shapes: `statements` and `conditionRows` camel-cased as `/statements` and
 *  `/condition-fields` arrive today, `readModel` and `previousVerdicts` RAW like
 *  `/read-model` (their object keys carry `framework:article` and article numbers). */
export interface DeclarationFrameworkSlice {
  declarationId: string
  framework: string
  /** The Step B version the slice was read from; null when it came from the JSON. */
  revision: { versionId: string; writeSeq: number } | null
  statements: StatementGroup
  readModel: DeclarationReadModel
  conditionRows: ConditionFieldRow[]
  /** The supplier pool's article numbers, in pool order. */
  poolNumbers: string[]
  previousVerdicts: Record<string, string | null>
  supplierMatched: boolean
  /** `/scope`'s candidates, filled only when the pool is empty: the screen's
   *  `poolNumbers` is `poolNumbers.length ? poolNumbers : candidateNumbers`. */
  candidateNumbers: string[]
}

interface FrameworkSliceWire {
  declaration_id: string
  framework: string
  revision: { version_id: string; write_seq: number } | null
  statements: unknown
  read_model: DeclarationReadModel
  condition_rows: unknown[]
  pool_numbers?: string[]
  previous_verdicts?: Record<string, string | null>
  supplier_matched?: boolean
  candidate_numbers?: string[]
  /** `?rows=columnar` (TUR-1725): the same rows as columns; `condition_rows` is then
   *  []. Absent from an older backend, which sends `condition_rows` instead. */
  condition_rows_columnar?: ConditionRowsColumnarWire | null
}

/** `ConditionRowsColumnarOut`: row i is `a[i] c[i] f[i] k[i] v[i]`. `a` indexes the
 *  response's `pool_numbers` below its length and `article_extra` from there on; `c`,
 *  `f`, `k`, `v` index `cas`, `frameworks`, `field_keys`, `values`, -1 meaning null. */
export interface ConditionRowsColumnarWire {
  article_extra: string[]
  cas: string[]
  frameworks: string[]
  field_keys: string[]
  values: string[]
  a: number[]
  c: number[]
  f: number[]
  k: number[]
  v: number[]
}

/** The columns back into exactly the rows `/condition-fields` camel-cases today, in the
 *  server's order (the derivation reads the FIRST row of an address). Built directly:
 *  the five keys are already the client's, so no key conversion runs over 50k rows. */
export function decodeConditionRows(
  col: ConditionRowsColumnarWire,
  poolNumbers: readonly string[],
): ConditionFieldRow[] {
  const base = poolNumbers.length
  const { article_extra, cas, frameworks, field_keys, values } = col
  const { a, c, f, k, v } = col
  const rows: ConditionFieldRow[] = new Array(a.length)
  for (let i = 0; i < a.length; i++) {
    const ai = a[i]
    rows[i] = {
      articleNumber: ai < base ? poolNumbers[ai] : article_extra[ai - base],
      substanceCas: c[i] === -1 ? null : cas[c[i]],
      framework: f[i] === -1 ? null : frameworks[f[i]],
      fieldKey: field_keys[k[i]],
      value: v[i] === -1 ? null : values[v[i]],
    }
  }
  return rows
}

/** What `apiClient`'s response interceptor does to an ordinary read: keys to camelCase,
 *  then date strings to dates — applied to the two parts that are read that way today. */
function asOrdinaryRead<T>(data: unknown): T {
  return convertDateStringsToDates(convertKeysToCamelCase(data)) as T
}

export function toFrameworkSlice(
  wire: FrameworkSliceWire,
): DeclarationFrameworkSlice {
  return {
    declarationId: wire.declaration_id,
    framework: wire.framework,
    revision: wire.revision
      ? {
          versionId: wire.revision.version_id,
          writeSeq: wire.revision.write_seq,
        }
      : null,
    statements: asOrdinaryRead<StatementGroup>(wire.statements),
    readModel: wire.read_model,
    // The columns when the server sent them; an older backend (mid-deploy) ignores
    // `?rows=columnar` and sends the list, read as before.
    conditionRows: wire.condition_rows_columnar
      ? decodeConditionRows(
          wire.condition_rows_columnar,
          wire.pool_numbers ?? [],
        )
      : asOrdinaryRead<ConditionFieldRow[]>(wire.condition_rows ?? []),
    poolNumbers: wire.pool_numbers ?? [],
    previousVerdicts: wire.previous_verdicts ?? {},
    supplierMatched: wire.supplier_matched ?? false,
    candidateNumbers: wire.candidate_numbers ?? [],
  }
}

function frameworkSlicePrefix(declId: string) {
  return ["mc", "declarations", declId, "framework"] as const
}

export function frameworkSliceKey(declId: string, framework: string) {
  return [...frameworkSlicePrefix(declId), framework] as const
}

const SLICE_ENCODING = { rows: "columnar", verdicts: "slim" } as const

function fetchFrameworkSlice(
  declId: string,
  framework: string,
  signal?: AbortSignal,
): Promise<DeclarationFrameworkSlice> {
  return apiClientV2
    .get<FrameworkSliceWire>(
      `/material-compliance/declarations/${declId}/frameworks/${encodeURIComponent(framework)}`,
      {
        // TUR-1725: the rows as columns and each verdict as `{verdict, tier}` (all the
        // screen reads of one) — the same answer in a fraction of the bytes.
        params: SLICE_ENCODING,
        skipCaseConversion: true,
        signal,
      } as AxiosRequestConfig & {
        skipCaseConversion?: boolean
      },
    )
    .then((r) => toFrameworkSlice(r.data))
}

/** How long a loaded slice (and the memory hints) counts as fresh (Step C3): switching
 *  back to a regulation seen moments ago, or opening the prefetched one, reads the
 *  cache instead of downloading megabytes again. Every write still refreshes it at
 *  once (the gestures' invalidations), so this only spares unchanged re-reads. */
const SLICE_STALE_MS = 30_000

/** A 4xx is final (an older backend without the route, an unknown regulation):
 *  the caller falls back at once instead of after the retries. */
function retryUnless4xx(failureCount: number, error: unknown): boolean {
  const status = getApiErrorStatus(error)
  if (status !== null && status >= 400 && status < 500) return false
  return failureCount < 2
}

/** One regulation's review reads (C3). Keyed under the declaration's prefix, so every
 *  gesture's prefix invalidation and the hydration list refresh it too. A visited
 *  regulation leaves the cache a minute after its last observer, not after five. */
export function useDeclarationFramework(
  declId: string,
  framework: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  return useQuery({
    queryKey: frameworkSliceKey(declId, framework ?? ""),
    // The signal: a regulation the reviewer has already left stops downloading
    // (React Query cancels a read whose last observer is gone when it took one).
    queryFn: ({ signal }) =>
      fetchFrameworkSlice(declId, framework ?? "", signal),
    enabled: enabled && !!declId && !!framework,
    gcTime: 60_000,
    staleTime: SLICE_STALE_MS,
    retry: retryUnless4xx,
    ...REVIEW_READ_OPTIONS,
    // A slice is replaced whole: deep-comparing tens of thousands of rows against the
    // previous answer on every refetch costs more than it saves.
    structuralSharing: false,
  })
}

/** A regulation's slice for a gesture that acts on a regulation that is not on screen
 *  (C3-b3): `fetchQuery`, never `ensureQueryData`, which would hand back a cached
 *  slice a gesture has invalidated. */
export function loadFrameworkSlice(
  queryClient: QueryClient,
  declId: string,
  framework: string,
  /** 0 (the default): a cached slice is fetched again — what a gesture that WRITES on
   *  its basis needs. A display read may accept a slice that is a few seconds old. */
  options: { staleTime?: number } = {},
): Promise<DeclarationFrameworkSlice> {
  const load = () =>
    queryClient.fetchQuery({
      queryKey: frameworkSliceKey(declId, framework),
      queryFn: () => fetchFrameworkSlice(declId, framework),
      gcTime: 60_000,
      staleTime: options.staleTime ?? 0,
      structuralSharing: false,
      // No retries: a gesture reports the regulations it could not load by name, at
      // once, rather than after three attempts and a server-error toast for each.
      retry: false,
    })
  // `fetchQuery` can join a read the page already started for the same regulation;
  // when the page's last reader leaves, that read is cancelled and takes this one
  // with it. A gesture still needs the slice: ask once more, as a read of its own.
  return load().catch((error: unknown) => {
    if (isCancelledError(error)) return load()
    throw error
  })
}

/** Warm the slice of the regulation the reviewer is likely to open next (C3-b2):
 *  the same key, query and short gcTime as `useDeclarationFramework`, so switching
 *  to it paints at once. Called only after the open regulation's slice settled, so
 *  the two never compete on the server. */
export function prefetchFrameworkSlice(
  queryClient: QueryClient,
  declId: string,
  framework: string,
): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: frameworkSliceKey(declId, framework),
    queryFn: () => fetchFrameworkSlice(declId, framework),
    gcTime: 60_000,
    // A slice already in the cache and fresh is not downloaded a second time.
    staleTime: SLICE_STALE_MS,
    structuralSharing: false,
    retry: retryUnless4xx,
  })
}

/** `/scope` for a gesture that needs the supplier pool with ids and names while the
 *  paged review has not loaded it yet (C3-b2): same key and query as
 *  `useDeclarationScope`, `fetchQuery` so an invalidated pool refetches. */
export function loadDeclarationScope(
  queryClient: QueryClient,
  declId: string,
): Promise<DeclarationScopeOut | null> {
  return queryClient.fetchQuery({
    queryKey: ["mc", "declarations", declId, "scope"],
    queryFn: async (): Promise<DeclarationScopeOut | null> =>
      apiClientV2
        .get(`/material-compliance/declarations/${declId}/scope`)
        .then((r) => r.data),
    // Paged review only: keep the query's meta (a fetch replaces the options).
    meta: PAGED_SCOPE_META,
  })
}

/** GET /{id}/memory-hints — `/condition-fields`' memory hints on their own (C3): they
 *  are declaration-wide, so no regulation's slice carries them. */
function memoryHintsKey(declId: string) {
  return ["mc", "declarations", declId, "memory-hints"]
}

export function useDeclarationMemoryHints(
  declId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  return useQuery({
    queryKey: memoryHintsKey(declId),
    queryFn: () =>
      apiClientV2
        .get<{
          declarationId: string
          memory: DeclarationConditionFields["memory"]
        }>(`/material-compliance/declarations/${declId}/memory-hints`)
        .then((r) => r.data?.memory ?? []),
    enabled: enabled && !!declId,
    staleTime: SLICE_STALE_MS,
    retry: retryUnless4xx,
    ...REVIEW_READ_OPTIONS,
  })
}

/** A condition-field cell's address — `row_identity` on the server. JSON so a null
 *  substance or framework never collides with an empty string. */
function conditionRowAddress(r: {
  articleNumber: string
  substanceCas?: string | null
  framework?: string | null
  fieldKey: string
}): string {
  return JSON.stringify([
    r.articleNumber,
    r.substanceCas ?? null,
    r.framework ?? null,
    r.fieldKey,
  ])
}

/** A regulation's slice holds its own rows and every framework-less one (the
 *  server's `framework = :fw OR framework IS NULL`). */
function rowBelongsTo(
  r: { framework?: string | null },
  framework: string,
): boolean {
  return r.framework == null || r.framework === framework
}

/** PATCH `?echo=changed` (C3-b1, paged review): what a values-save changed. */
export interface ConditionFieldsEcho {
  echo: "changed"
  /** Every persisted row at each address whose rows changed. */
  rows: ConditionFieldRow[]
  /** The changed addresses that hold no row any more (a withdrawn sign-off among them). */
  removed: Omit<ConditionFieldRow, "value">[]
  /** This save's place in the declaration's save order (taken under the declaration
   *  lock every condition write holds); null from a backend that does not send it. */
  saveSeq: number | null
}

/** What the slices of one declaration know about the order of its condition writes.
 *  `bySeq` is, per address, the save order of the write the cache last took it from;
 *  `floor` the order of the last whole table applied (true for every address not in
 *  `bySeq` above it); `maxSeen` the latest write any response carried. `dirty` is set
 *  whenever the cache may not equal the server — an echo or table that lost the race at
 *  some address, arrived after a later one, carried no order, or raced an in-flight
 *  slice read — and is settled by ONE slice refetch when the declaration's last
 *  overlapping condition write settles (`settleConditionWrite`). Keyed by `QueryClient`
 *  so tests with a fresh client stay isolated. */
interface ConditionOrder {
  bySeq: Map<string, number>
  floor: number
  maxSeen: number
  dirty: boolean
}

const conditionOrders = new WeakMap<QueryClient, Map<string, ConditionOrder>>()

function conditionOrder(
  queryClient: QueryClient,
  declarationId: string,
): ConditionOrder {
  let byDecl = conditionOrders.get(queryClient)
  if (!byDecl) {
    byDecl = new Map()
    conditionOrders.set(queryClient, byDecl)
  }
  let order = byDecl.get(declarationId)
  if (!order) {
    order = {
      bySeq: new Map(),
      floor: -Infinity,
      maxSeen: -Infinity,
      dirty: false,
    }
    byDecl.set(declarationId, order)
  }
  return order
}

/** Stop every in-flight read of the declaration's slices (and its memory hints) before a
 *  write into them: a read that started before the save would otherwise land after it
 *  and put the pre-save rows back. The default (reverting) cancel keeps a write made
 *  meanwhile: `setQueryData` moves the query's restore point. A cancelled read never
 *  completes on its own, so the cache is marked dirty and `settleConditionWrite`
 *  refetches both the slices and the memory hints once the writes settle. */
function stopSliceReads(queryClient: QueryClient, declarationId: string) {
  const keys = [
    frameworkSlicePrefix(declarationId),
    memoryHintsKey(declarationId),
  ]
  if (keys.some((queryKey) => queryClient.isFetching({ queryKey }) > 0)) {
    conditionOrder(queryClient, declarationId).dirty = true
    for (const queryKey of keys) void queryClient.cancelQueries({ queryKey })
  }
}

/** The whole persisted table (a values PUT, a verify) is the truth for every cached
 *  slice of the declaration — at every address no LATER echo has already written. Each
 *  slice takes its own rows from it, in the table's order. `seq` is the write's place in
 *  the save order; without one (today's unpaged save, an older backend) the table is
 *  applied whole, as before ordering existed. */
export function cacheConditionRowsInSlices(
  queryClient: QueryClient,
  declarationId: string,
  rows: readonly ConditionFieldRow[],
  seq: number | null = null,
) {
  const order = conditionOrder(queryClient, declarationId)
  if (seq !== null) {
    if (seq < order.maxSeen) order.dirty = true
    order.maxSeen = Math.max(order.maxSeen, seq)
    if (seq <= order.floor) {
      // An older whole table than one already applied: nothing in it is news.
      order.dirty = true
      return
    }
    order.floor = seq
  }
  const newer =
    seq === null
      ? new Set<string>()
      : new Set(
          [...order.bySeq]
            .filter(([, s]) => s > seq)
            .map(([address]) => address),
        )
  if (newer.size) order.dirty = true
  for (const [address, s] of [...order.bySeq])
    if (seq === null || s <= seq) order.bySeq.delete(address)
  for (const [
    key,
    slice,
  ] of queryClient.getQueriesData<DeclarationFrameworkSlice>({
    queryKey: frameworkSlicePrefix(declarationId),
  })) {
    if (!slice) continue
    // A newer echo's address keeps the slice's own rows, at the place the table holds
    // that address (or at the end when the table no longer has it), so row order
    // stays the table's.
    const kept = new Map<string, ConditionFieldRow[]>()
    for (const r of slice.conditionRows) {
      const address = conditionRowAddress(r)
      if (newer.has(address))
        kept.set(address, [...(kept.get(address) ?? []), r])
    }
    const merged: ConditionFieldRow[] = []
    for (const r of rows) {
      if (!rowBelongsTo(r, slice.framework)) continue
      const address = conditionRowAddress(r)
      if (!newer.has(address)) merged.push(r)
      else if (kept.has(address)) {
        merged.push(...(kept.get(address) ?? []))
        kept.delete(address)
      }
    }
    for (const leftover of kept.values()) merged.push(...leftover)
    queryClient.setQueryData<DeclarationFrameworkSlice>(key, {
      ...slice,
      conditionRows: merged,
    })
  }
}

/** Apply a save echo to every cached slice of the declaration, ADDRESS BY ADDRESS: an
 *  echoed address is taken from this echo only when no later write already set it (an
 *  echo, or a whole table past this save). Never dropped whole — the addresses a later
 *  save did not touch still carry this save's change. Returns how many addresses were
 *  taken. Also drops the memory hints of every concentration cell the echo moved, the
 *  rule `mergeConditionFieldsAfterSave` applies to a whole table. */
export function applyConditionEcho(
  queryClient: QueryClient,
  declarationId: string,
  echo: ConditionFieldsEcho,
): number {
  const order = conditionOrder(queryClient, declarationId)
  const seq = echo.saveSeq
  if (seq === null) order.dirty = true
  else {
    if (seq < order.maxSeen) order.dirty = true
    order.maxSeen = Math.max(order.maxSeen, seq)
  }
  const echoed = new Set([
    ...echo.rows.map(conditionRowAddress),
    ...echo.removed.map(conditionRowAddress),
  ])
  const taken = new Set<string>()
  for (const address of echoed) {
    const prior = Math.max(order.bySeq.get(address) ?? -Infinity, order.floor)
    if (seq !== null && seq <= prior) {
      order.dirty = true
      continue
    }
    taken.add(address)
    if (seq !== null) order.bySeq.set(address, seq)
  }
  if (taken.size) {
    for (const [
      key,
      slice,
    ] of queryClient.getQueriesData<DeclarationFrameworkSlice>({
      queryKey: frameworkSlicePrefix(declarationId),
    })) {
      if (!slice) continue
      queryClient.setQueryData<DeclarationFrameworkSlice>(key, {
        ...slice,
        conditionRows: [
          ...slice.conditionRows.filter(
            (r) => !taken.has(conditionRowAddress(r)),
          ),
          ...echo.rows.filter(
            (r) =>
              rowBelongsTo(r, slice.framework) &&
              taken.has(conditionRowAddress(r)),
          ),
        ],
      })
    }
  }
  const movedConcentrations = new Set(
    [...echo.rows, ...echo.removed]
      .filter(
        (r) =>
          r.fieldKey === "concentration" && taken.has(conditionRowAddress(r)),
      )
      .map((r) => concentrationCellKey(r)),
  )
  if (movedConcentrations.size)
    queryClient.setQueryData<ConcentrationMemoryHint[]>(
      memoryHintsKey(declarationId),
      (memory) =>
        memory?.filter(
          (h) => !movedConcentrations.has(concentrationCellKey(h)),
        ),
    )
  return taken.size
}

/** After the LAST of a declaration's overlapping condition writes (values saves and
 *  verifies) settles: if the cache may differ from the server (`ConditionOrder.dirty`),
 *  refetch its slices once, so the screen ends on the server's table. */
function settleConditionWrite(queryClient: QueryClient, declarationId: string) {
  const forDecl = (m: { state: { variables: unknown } }) =>
    (m.state.variables as { declarationId?: string } | undefined)
      ?.declarationId === declarationId
  const inFlight =
    queryClient.isMutating({
      mutationKey: SAVE_CONDITION_VALUES_MUTATION_KEY,
      predicate: forDecl,
    }) +
    queryClient.isMutating({
      mutationKey: VERIFY_STATEMENT_MUTATION_KEY,
      predicate: forDecl,
    })
  // `isMutating` still counts the caller while its `onSettled` runs.
  if (inFlight > 1) return
  const order = conditionOrder(queryClient, declarationId)
  // Nothing in flight: every later response carries a higher place than any recorded
  // here, so the per-address record can go (it grows with every echoed address).
  order.bySeq.clear()
  if (!order.dirty) return
  order.dirty = false
  void queryClient.invalidateQueries({
    queryKey: frameworkSlicePrefix(declarationId),
  })
  void queryClient.invalidateQueries({
    queryKey: memoryHintsKey(declarationId),
    exact: true,
  })
}

/** Snapshot of every cached slice of a declaration, taken before an optimistic
 *  statement edit so its `onError` can restore that one statement. */
type SliceSnapshot = [
  readonly unknown[],
  DeclarationFrameworkSlice | undefined,
][]

function patchStatementInSlices(
  queryClient: QueryClient,
  declarationId: string,
  statementId: string,
  patch: Parameters<typeof patchStatement>[2],
  opts: { pending: boolean },
): SliceSnapshot {
  // A slice read already on its way would land after the patch and wipe it; the
  // edit's own settle refetches the slices (`STATEMENT_CHANGE_READS`).
  void queryClient.cancelQueries({
    queryKey: frameworkSlicePrefix(declarationId),
  })
  const before = queryClient.getQueriesData<DeclarationFrameworkSlice>({
    queryKey: frameworkSlicePrefix(declarationId),
  })
  for (const [key, slice] of before) {
    if (!slice) continue
    const patched = patchStatement([slice.statements], statementId, patch, opts)
    if (patched && patched[0] !== slice.statements)
      queryClient.setQueryData<DeclarationFrameworkSlice>(key, {
        ...slice,
        statements: patched[0],
      })
  }
  return before
}

function restoreStatementInSlices(
  queryClient: QueryClient,
  snapshot: SliceSnapshot | undefined,
  statementId: string,
) {
  for (const [key, previous] of snapshot ?? []) {
    const current = queryClient.getQueryData<DeclarationFrameworkSlice>(key)
    if (!current || !previous) continue
    const restored = restoreStatementFromSnapshot(
      [current.statements],
      [previous.statements],
      statementId,
    )
    if (restored && restored[0] !== current.statements)
      queryClient.setQueryData<DeclarationFrameworkSlice>(key, {
        ...current,
        statements: restored[0],
      })
  }
}

/** SHA-256 (hex) over the distinct article numbers, sorted, joined by newlines —
 *  byte-for-byte the backend's `article_set_digest`, so the verify command can check
 *  it is about to sign exactly the cells this screen showed. */
export async function articleSetDigest(
  articleIds: readonly string[],
): Promise<string> {
  const text = [...new Set(articleIds)].sort().join("\n")
  const hash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  )
  return Array.from(new Uint8Array(hash), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("")
}

/** A verify's place in the save order, beside the rows it answered with: callers keep
 *  `mutateAsync` resolving to the rows alone. */
const verifySaveSeqs = new WeakMap<ConditionFieldRow[], number | null>()

export function useVerifyStatement() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationKey: VERIFY_STATEMENT_MUTATION_KEY,
    mutationFn: async (
      input: VerifyStatementIn,
    ): Promise<ConditionFieldRow[]> => {
      // ONE command: the SERVER derives this statement's open cells (the approve
      // gate's own derivation, `_open_review_work`) and signs exactly those — the
      // screen used to build one `__verified__` row per open cell and PUT the whole
      // condition table, which a 10k-article blanket statement could not fit
      // through the 5,000-row cap. The body is only a count and a digest of the
      // cells this screen showed: a sign-off means a person checked them, so the
      // server refuses (STALE_VIEW) when its set differs from what was on screen.
      const url = `/material-compliance/declarations/statements/${input.statementId}/verify`
      const body = {
        expectedCount: new Set(input.articleIds).size,
        expectedDigest: await articleSetDigest(input.articleIds),
      }
      // `?seq=true` only on the paged review: the default call is today's, untouched.
      const { data } = input.saveOrder
        ? await apiClientV2.post<
            ConditionFieldRowsOut & { saveSeq?: number | null }
          >(url, body, { params: { seq: true } })
        : await apiClientV2.post<ConditionFieldRowsOut>(url, body)
      const rows = data?.rows ?? []
      verifySaveSeqs.set(
        rows,
        (data as { saveSeq?: number | null } | undefined)?.saveSeq ?? null,
      )
      return rows
    },
    onSuccess: (rows, input) => {
      // The table carries its place in the save order, so a values-save echo that
      // lands after it can only overwrite the addresses it really changed later.
      cacheConditionRowsFromServer(
        queryClient,
        input.declarationId,
        rows,
        verifySaveSeqs.get(rows) ?? null,
      )
      // An empty cone today (only its two bookends), but a cone all the same.
      announceRecompute(input.declarationId)
      // A `useSaveConditionFields`/`useSaveConditionValues` response that lands
      // AFTER this one (a save the reviewer fired just before Verify, still
      // in flight) would otherwise leave its own, now-stale rows as the last
      // write to this key — invalidate so the next read refetches instead of
      // trusting whichever response happened to land last.
      queryClient.invalidateQueries({
        queryKey: conditionFieldsKey(input.declarationId),
        exact: true,
      })
    },
    onError: (error, input) => {
      // STALE_VIEW: the open cells moved since this screen rendered. Retrying would
      // send the same stale digest, so refresh the review reads instead: the
      // reviewer sees the new set and signs again, knowingly.
      // TUR-1900: NOTHING_TO_VERIFY (signed in another tab, or closed itself) and
      // NEEDS_INPUT (a cell now lacks a value) are the same disagreement between
      // the screen and the server: refresh, so the screen stops showing the old
      // state (the regulation unchecked, the statement still open).
      const code = getApiErrorCode(error)
      if (
        code === "STALE_VIEW" ||
        code === "NOTHING_TO_VERIFY" ||
        code === "NEEDS_INPUT"
      )
        invalidateAfterStatementChange(
          queryClient,
          input.declarationId,
          input.statementId,
        )
    },
    onSettled: (_data, _error, input) =>
      settleConditionWrite(queryClient, input.declarationId),
    // NO TOAST HERE: this mutation's callers (`signStep3`) own the message —
    // they know whether the refusal was NEEDS_INPUT, NOTHING_TO_VERIFY, a
    // retried-out STALE_STATE/REVIEW_BUSY or a plain failure, and word it
    // accordingly. A toast fired here as well would be a SECOND toast on top
    // of theirs. REVIEW_BUSY (TUR-0000 B3: the declaration lock this command
    // takes can lose to a busy row) retries exactly like STALE_STATE — both
    // are "try again", once here, immediately. `signStep3` then waits and signs
    // again (5 s, 15 s, TUR-1900) before it gives up.
    retry: (failureCount, error) =>
      failureCount < 1 &&
      (getApiErrorCode(error) === "STALE_STATE" ||
        getApiErrorCode(error) === "REVIEW_BUSY"),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export function useReviewField() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: ReviewFieldIn): Promise<void> => {
      // Declaration id in PATH, field_key in BODY (auto snake_cased by client).
      await apiClientV2.post(
        `/material-compliance/declarations/${input.declarationId}/fields/review`,
        {
          fieldKey: input.fieldKey,
          action: input.action,
          value: input.newValue,
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export function useReviewStatement() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: ReviewStatementIn): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/statements/${input.statementId}/review`,
        { action: input.action },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export function useUpdateScope() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: UpdateScopeIn): Promise<void> => {
      // Backend UpdateScopeIn wants { mode, article_numbers } (not the full input).
      // The camelCase interceptor converts: mode→mode, articleNumbers→article_numbers.
      await apiClientV2.put(
        `/material-compliance/declarations/${input.declarationId}/scope`,
        {
          mode: input.scopeMode,
          articleNumbers: input.selectedArticleNumbers,
        },
      )
    },
    onSuccess: (_data, variables) => {
      invalidateDeclaration(queryClient, variables.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/**
 * ONE onError HERE COVERS EVERY CALLER. `UnitCard`, `ArticleSlot` (both the
 * staged `list` Apply and the immediate `group`/`material` write) and
 * `SupplierAssignControl`'s neighbours all fire this with `.mutate()` and no
 * options of their own — before this, a rejection (a stale `listVersion`, a
 * removed statement, a network blip) left the picker showing the value the
 * reviewer just chose with nothing actually written, and the ONLY signal was
 * a >=500 (the global interceptor's own toast, `errorToast.ts`); every other
 * status failed in total silence. `toastApiError` skips exactly that case so
 * this never stacks a second toast on top of it.
 */
/**
 * RECORD A STATEMENT THE PIPELINE NEVER EXTRACTED — "+ Aussage hinzufügen"
 * (TUR-1445). The server writes the interface group AND the staged row in one
 * gesture and records the `included` provenance note at the new address, so the
 * created card renders, blocks approve, and carries its "Manuell ergänzt" badge
 * with no further client work. `mutateAsync` on purpose: the dialog needs the
 * created identity (`statementId`) to land the mask on the new card.
 */
export function useCreateStatement() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  return useMutation({
    mutationFn: async (input: CreateStatementIn): Promise<CreatedStatement> => {
      // Backend CreateStatementIn is snake_case; the camelCase interceptor
      // converts scopeAll/articleNumbers/statementText (and the response's
      // statement_id/group_index) both ways.
      const { data } = await apiClientV2.post<CreatedStatement>(
        `/material-compliance/declarations/${input.declarationId}/statements`,
        {
          framework: input.framework,
          verdict: input.verdict,
          scopeAll: input.scopeAll,
          articleNumbers: input.articleNumbers,
          statementText: input.statementText,
          evidence: input.evidence,
        },
      )
      return data
    },
    onSuccess: (_data, variables) => {
      invalidateDeclaration(queryClient, variables.declarationId)
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_DECL_V2_ADD_STATEMENT_FAILED")
    },
  })
}

type StatementScopeGeneration = {
  previous: StatementGroup[] | undefined
  generation: number
  /** The cached regulation slices before the edit (C3-b1), for the same rollback. */
  slices?: SliceSnapshot
}

/**
 * The optimistic/restore/settle triad shared by `useUpdateStatementScope` and
 * `useApplyStatementPoolScope` — the two statement-scope writers differ only in
 * their wire call (`PUT .../scope` with a list vs `POST .../scope/apply-pool`
 * with no body); everything about how the cache reacts is identical, so it is
 * written once here instead of copied.
 *
 * OPTIMISTIC (TUR-1211, W5-5): the new `articleNumbers` show on the card before
 * the write resolves, so the Geltung switch repaints on the click, not on the
 * round trip. `onError` restores this statement's row from the snapshot
 * `onMutate` captured, but ONLY when no newer mutation has started on the SAME
 * statement since (guarded by `isLatestStatementGeneration`, shared with
 * `useEditStatementVerdict`), otherwise a slow failure here would clobber
 * whatever that newer mutation already wrote, and `onSettled`'s
 * `invalidateAfterStatementChange` is left to reconcile the stale failure
 * instead. The restore itself touches only this statement's row
 * (`restoreStatementFromSnapshot`), never the whole cached array, so a
 * concurrent edit on a DIFFERENT statement is untouched either way.
 * `onSettled` narrows the refetch to `invalidateAfterStatementChange`, see
 * that function's own doc for why this is the one exception to
 * `invalidateDeclaration`.
 */
function useStatementScopeCacheHandlers() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()

  const onMutate = async (input: {
    declarationId: string
    statementId: string
    articleNumbers: string[]
  }): Promise<StatementScopeGeneration> => {
    const key = statementsQueryKey(input.declarationId)
    await queryClient.cancelQueries({ queryKey: key })
    const generation = nextStatementGeneration(queryClient, input.statementId)
    const previous = queryClient.getQueryData<StatementGroup[]>(key)
    queryClient.setQueryData<StatementGroup[] | undefined>(key, (data) =>
      patchStatement(
        data,
        input.statementId,
        { scopeAll: false, articleNumbers: input.articleNumbers },
        { pending: true },
      ),
    )
    const slices = patchStatementInSlices(
      queryClient,
      input.declarationId,
      input.statementId,
      { scopeAll: false, articleNumbers: input.articleNumbers },
      { pending: true },
    )
    return { previous, generation, slices }
  }

  const onError = (
    error: unknown,
    variables: { declarationId: string; statementId: string },
    context: StatementScopeGeneration | undefined,
  ) => {
    if (
      context &&
      isLatestStatementGeneration(
        queryClient,
        variables.statementId,
        context.generation,
      )
    ) {
      if (context.previous !== undefined) {
        const key = statementsQueryKey(variables.declarationId)
        queryClient.setQueryData(
          key,
          restoreStatementFromSnapshot(
            queryClient.getQueryData<StatementGroup[]>(key),
            context.previous,
            variables.statementId,
          ),
        )
      }
      // The slices roll back on their own: the paged review has no `/statements`.
      restoreStatementInSlices(
        queryClient,
        context.slices,
        variables.statementId,
      )
    }
    toastApiError(error, translate, "MC_DECL_V2_SCOPE_SAVE_FAILED")
  }

  const onSettled = (
    _data: void | undefined,
    _error: unknown,
    variables: { declarationId: string; statementId: string },
    context: StatementScopeGeneration | undefined,
  ) => {
    // Only the LAST-STARTED mutation on this statement reconciles with the
    // server. If mutation A settles while a newer mutation B on the SAME
    // statement is still pending, A's refetch would overwrite B's
    // optimistic write and clear B's `pending` flag; B's own onSettled
    // does the reconciliation instead (W5-5 review finding 8).
    if (
      context &&
      isLatestStatementGeneration(
        queryClient,
        variables.statementId,
        context.generation,
      )
    ) {
      invalidateAfterStatementChange(
        queryClient,
        variables.declarationId,
        variables.statementId,
      )
    }
  }

  return { onMutate, onError, onSettled }
}

/**
 * `UnitCard`'s "Alle Artikel" write, `ArticleListSlot`/`ArticleBrowserSheet`
 * Apply and `ArticleSlot`'s Mine toggle, whenever the reviewer picked a STRICT
 * SUBSET of the pool (a whole-pool pick goes through `useApplyStatementPoolScope`
 * instead). Wire body is `{ articleNumbers }` only — see that hook's own doc for
 * why "Alle Artikel" is a separate route rather than a flag on this one.
 */
export function useUpdateStatementScope() {
  const { onMutate, onError, onSettled } = useStatementScopeCacheHandlers()
  const mutation = useMutation<
    void,
    unknown,
    UpdateStatementScopeIn,
    StatementScopeGeneration
  >({
    mutationFn: async (input: UpdateStatementScopeIn): Promise<void> => {
      await apiClientV2.put(
        `/material-compliance/declarations/statements/${input.statementId}/scope`,
        { articleNumbers: input.articleNumbers },
      )
    },
    onMutate,
    onSuccess: (_data, input) => announceRecompute(input.declarationId),
    onError,
    onSettled,
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/**
 * `POST .../scope/apply-pool` — "Alle Artikel" as a body-less command: the
 * server resolves the universe itself (supplier pool, else the declaration's
 * linked live articles). NO LIST SHIPS, on purpose — an org with 10,582
 * articles broke the old `PUT .../scope { articleNumbers: [...pool] }` against
 * the list's 10,000 cap, and a later attempt to route "all" through that same
 * PUT via a `mode` flag turned out to fail CLOSED on an old backend: it read
 * the unknown field as `extra='ignore'` and applied an EMPTY scope (deploy
 * skew, 2026-09-28). A dedicated route with no body answers 404 on an old
 * backend instead, which is a refusal, not a silent data loss.
 *
 * `poolNumbers` feeds only the optimistic patch (the card shows the snapshot
 * immediately, while the server resolves the same set itself); it never
 * reaches the wire.
 */
export function useApplyStatementPoolScope() {
  const { onMutate, onError, onSettled } = useStatementScopeCacheHandlers()
  const mutation = useMutation<
    void,
    unknown,
    ApplyStatementPoolScopeIn,
    StatementScopeGeneration
  >({
    mutationFn: async (input: ApplyStatementPoolScopeIn): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/statements/${input.statementId}/scope/apply-pool`,
      )
    },
    onMutate: (input) =>
      onMutate({
        declarationId: input.declarationId,
        statementId: input.statementId,
        articleNumbers: input.poolNumbers,
      }),
    onSuccess: (_data, input) => announceRecompute(input.declarationId),
    onError,
    onSettled,
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/** What one slot-wide pick did (TUR-1725). `noSlot` / `notFound`: articles of the
 *  slot list the server skipped (no slot in the record / not addressable any more),
 *  the rest still applied; optional for a backend that predates them. */
export interface SubstancePickResult {
  applied: number
  alreadySet: number
  noSlot?: number
  notFound?: number
  /** The first few skipped articles of each kind, by number. */
  noSlotArticles?: string[]
  notFoundArticles?: string[]
}

/** Is this the answer of a backend that has no batch route yet (404/405 from
 *  the router, a plain `detail` string), rather than the batch route's own
 *  refusal (a 404 ARTICLE_NOT_FOUND / STATEMENT_NOT_FOUND carries a code)? */
function batchRouteMissing(error: unknown): boolean {
  const status = getApiErrorStatus(error)
  return (status === 404 || status === 405) && getApiErrorCode(error) === null
}

/** A reviewer chooses which registry substance a slot's values are about,
 *  for a slot the extraction left without one (TUR-1211, W4-1b): the
 *  companion gesture to `ArticleBindControl`'s article binding above, but
 *  for the substance axis of the same address. The server rekeys the slot's
 *  cells server-side and rematerialises the condition-field rows; nothing
 *  here writes a row directly (mirrors `SubstanceEditor`'s own
 *  `latestRowsRef` rule for the binding gesture). Never gates approval: the
 *  footer's worklist derivation does not read this at all.
 *
 *  Uses `invalidateDeclaration`, not a hand-named subset of reads: both
 *  `statements` (`useDeclarationStatements`) and `condition-fields`
 *  (`useDeclarationConditionFields`) sit under its declaration prefix, and
 *  "no review mutation names individual reads any more" (this file's own
 *  regression guard, see `declarationsApi.test.tsx`) would fail on a
 *  hand-named key here exactly as it would anywhere else in this file. */
export function useSetConditionSubstance() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    // ONE request for the whole slot (TUR-1725): `.../conditions/substance/batch`
    // applies the pick to every article in one transaction and answers how many
    // it applied and how many already carried a substance (skipped, as the old
    // per-article loop skipped their SLOT_HAS_SUBSTANCE). The per-article loop
    // it replaces took ~13 s per article (~38 h at 10,582) and invalidated
    // every declaration read after each one.
    mutationFn: async (input: {
      declarationId: string
      statementId: string
      articleNumbers: readonly string[]
      substanceId: string
    }): Promise<SubstancePickResult> => {
      const base = `/material-compliance/declarations/${input.declarationId}/statements/${input.statementId}/conditions/substance`
      try {
        const { data } = await apiClientV2.post<SubstancePickResult>(
          `${base}/batch`,
          {
            articleNumbers: input.articleNumbers,
            substanceId: input.substanceId,
          },
        )
        return data
      } catch (error) {
        if (!batchRouteMissing(error)) throw error
      }
      // DEPLOY ORDER: a backend without the batch route (this frontend landed
      // first) gets the old per-article loop, exactly as before: in order, an
      // article already set (SLOT_HAS_SUBSTANCE) counted and skipped, any other
      // refusal stops the loop.
      let applied = 0
      let alreadySet = 0
      for (const articleNumber of input.articleNumbers) {
        try {
          await apiClientV2.post(base, {
            articleNumber,
            substanceId: input.substanceId,
          })
          applied += 1
        } catch (error) {
          if (getApiErrorCode(error) !== "SLOT_HAS_SUBSTANCE") throw error
          alreadySet += 1
        }
      }
      return { applied, alreadySet }
    },
    // On settled, not on success: a pick the server committed whose answer
    // was lost (network, proxy timeout) must still show as named, and a
    // refusal costs one refetch of reads it did not change.
    onSettled: (_data, _error, input) => {
      invalidateDeclaration(queryClient, input.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export type LinkSubstanceStatus =
  | "created"
  | "linked"
  | "already_linked"
  | "invalid_cas"
  | "name_required"
  | "create_unavailable"
  | "register_conflict"
  | "not_found"

export interface LinkSubstanceItem {
  framework: string
  groupIndex: number
  disclosureIndex: number
  /** Only used when the substance has to be created; the CAS is always the document's. */
  name?: string
}

export interface LinkSubstanceResult {
  framework: string
  groupIndex: number
  disclosureIndex: number
  status: LinkSubstanceStatus
  substanceId: string | null
}

export interface LinkSubstancesOutcome {
  results: LinkSubstanceResult[]
  /** False: saved, but the recompute that clears the finding did not start. */
  recomputeRouted: boolean
}

/** Create-and-link for disclosures the register could not connect: the server
 *  links an existing substance or creates the org's own, then recomputes so the
 *  open finding heals. New substances show up in master data, hence the
 *  substance caches. */
export function useLinkUnresolvedSubstances() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      items: LinkSubstanceItem[]
    }): Promise<LinkSubstancesOutcome> => {
      const r = await apiClientV2.post<Partial<LinkSubstancesOutcome>>(
        `/material-compliance/declarations/${input.declarationId}/substances/link`,
        { items: input.items },
      )
      return {
        results: r.data?.results ?? [],
        recomputeRouted: r.data?.recomputeRouted !== false,
      }
    },
    onSuccess: (_data, input) => {
      invalidateDeclaration(queryClient, input.declarationId)
      queryClient.invalidateQueries({ queryKey: ["mc", "substances"] })
      announceRecompute(input.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

/**
 * `UnitCard`'s own verdict select and `EditionRow` fire this with `.mutate()`
 * and no options of their own. `verdict: null` is documented as "leave the
 * stored verdict standing" (a list-version-only edit): the optimistic patch
 * below honours the same contract and never clears `verdictOverride` on a
 * null verdict.
 *
 * OPTIMISTIC (TUR-1211, W5-5): same shape as `useUpdateStatementScope`
 * above, the new verdict shows before the POST resolves, and `onError`
 * shares that hook's row-scoped, generation-guarded restore: it only rolls
 * back THIS statement's row, and only when no newer mutation has started on
 * the same statement since (`isLatestStatementGeneration`), so a verdict
 * edit that fails after a second edit on the same statement (or a different
 * one) never clobbers what the newer edit wrote. `onSettled` uses the
 * narrowed `invalidateAfterStatementChange` rather than
 * `invalidateDeclaration` (see that function's own doc).
 */
export function useEditStatementVerdict() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation<
    void,
    unknown,
    EditStatementVerdictIn,
    {
      previous: StatementGroup[] | undefined
      generation: number
      slices?: SliceSnapshot
    }
  >({
    mutationFn: async (input: EditStatementVerdictIn): Promise<void> => {
      // Backend wants { verdict, list_version }; the camelCase interceptor
      // converts listVersion → list_version.
      await apiClientV2.post(
        `/material-compliance/declarations/statements/${input.statementId}/verdict`,
        {
          verdict: input.verdict,
          listVersion: input.listVersion,
          ...(input.expectedVerdictOverride !== undefined && {
            expectedVerdictOverride: input.expectedVerdictOverride,
          }),
          ...(input.expectedListVersion !== undefined && {
            expectedListVersion: input.expectedListVersion,
          }),
        },
      )
    },
    onMutate: async (input) => {
      const key = statementsQueryKey(input.declarationId)
      await queryClient.cancelQueries({ queryKey: key })
      const generation = nextStatementGeneration(queryClient, input.statementId)
      const previous = queryClient.getQueryData<StatementGroup[]>(key)
      queryClient.setQueryData<StatementGroup[] | undefined>(key, (data) =>
        patchStatement(
          data,
          input.statementId,
          {
            verdictOverride: input.verdict ?? undefined,
            listVersion: input.listVersion,
          },
          { pending: true },
        ),
      )
      const slices = patchStatementInSlices(
        queryClient,
        input.declarationId,
        input.statementId,
        {
          verdictOverride: input.verdict ?? undefined,
          listVersion: input.listVersion,
        },
        { pending: true },
      )
      return { previous, generation, slices }
    },
    onError: (error, variables, context) => {
      if (
        context &&
        isLatestStatementGeneration(
          queryClient,
          variables.statementId,
          context.generation,
        )
      ) {
        if (context.previous !== undefined) {
          const key = statementsQueryKey(variables.declarationId)
          queryClient.setQueryData(
            key,
            restoreStatementFromSnapshot(
              queryClient.getQueryData<StatementGroup[]>(key),
              context.previous,
              variables.statementId,
            ),
          )
        }
        // The slices roll back on their own: the paged review has no `/statements`.
        restoreStatementInSlices(
          queryClient,
          context.slices,
          variables.statementId,
        )
      }
      // TUR-1927: another tab or a colleague changed this verdict since the screen
      // loaded; nothing was written. The reads refresh whatever generation this was
      // (`onSettled` refreshes only the latest), so the screen shows the current
      // verdict, and the reviewer is told why their choice did not stick.
      if (getApiErrorCode(error) === "STALE_VIEW") {
        invalidateAfterStatementChange(
          queryClient,
          variables.declarationId,
          variables.statementId,
        )
        toast.error(translate("MC_DECL_V2_VERDICT_STALE"))
        return
      }
      toastApiError(error, translate, "MC_DECL_V2_VERDICT_SAVE_FAILED")
    },
    onSettled: (_data, _error, variables, context) => {
      // Same generation guard as `useUpdateStatementScope` above: only the
      // last-started mutation on this statement invalidates, so a stale
      // settle never clobbers a still-pending newer mutation's optimistic
      // write (W5-5 review finding 8).
      if (
        context &&
        isLatestStatementGeneration(
          queryClient,
          variables.statementId,
          context.generation,
        )
      ) {
        invalidateAfterStatementChange(
          queryClient,
          variables.declarationId,
          variables.statementId,
        )
      }
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/**
 * `Confirm statement` — step 1's SIGNATURE, and the only step-1 gesture that fires a
 * cone. The verdict endpoint above is a SAVE: it commits the value to the graph and
 * moves nothing downstream.
 *
 * It sends NO BODY. A signature is over what is already on the record; letting the
 * client name the values it is signing would let a stale screen sign something the
 * server never showed.
 */
export function useConfirmStatement() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      statementId: string
    }): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/statements/${input.statementId}/confirm`,
      )
    },
    onSuccess: (_data, variables) => {
      // The signature is what the green rule compares against — refetch every read so
      // all surfaces agree on it, not just the statements list.
      invalidateDeclaration(queryClient, variables.declarationId)
      announceRecompute(variables.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/**
 * `Reset statement` — hands one statement back to the run's own extracted
 * proposal. The server drops the reviewer's verdict override and step-1 signature
 * and reverts what they had moved; the client names no values, so there is NO BODY
 * (same reasoning as `useConfirmStatement`: the server's record is the only truth).
 *
 * Invalidates on SETTLED, not on success: a refused reset (stale view, statement
 * already moved by someone else) is exactly the case where the screen's picture of
 * the statement is wrong, so it must refresh too.
 *
 * Announces the recompute on SUCCESS only: the server fires a confirm's cone behind
 * the restored proposal, and the page refetches when it settles; a refused reset
 * fired none.
 */
export function useResetStatement() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      statementId: string
    }): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/statements/${input.statementId}/reset`,
      )
    },
    onSuccess: (_data, variables) => announceRecompute(variables.declarationId),
    onSettled: (_data, _error, variables) => {
      invalidateDeclaration(queryClient, variables.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export interface ApproveDeclarationIn {
  declId: string
  /** Stage 1 (2026-09-17 decision): a supplier question batch no longer
   *  blocks approve outright, it holds up approve only until the reviewer
   *  explicitly acknowledges it, with a reason. Omitted (or false) sends NO
   *  body at all, so a declaration with no open batch keeps today's exact
   *  request shape. */
  acknowledgeOpenQuestions?: boolean
  /** Required by the server (>= 10 trimmed chars) whenever
   *  `acknowledgeOpenQuestions` is true; ignored otherwise. */
  reason?: string
}

/** What approving `declId` now would write, for the confirmation in front of the
 *  approve gesture. Asked only while that dialog is open (`enabled`) and fresh on
 *  every open, since an edit in between changes the answer.
 *
 *  Keyed OUTSIDE `["mc", "declarations", declId]` on purpose: the studio footer
 *  reads any fetch under that prefix as "saving" and would hold approve for it.
 *  Not retried: a refusal (an older backend's 404, a 403) falls back to the
 *  dialog's count-free copy at once instead of spinning for seconds. */
export function useApprovePreview(declId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["mc", "approve-preview", declId],
    queryFn: async (): Promise<ApprovePreview> => {
      const data = await apiClientV2
        .get(`/material-compliance/declarations/${declId}/approve/preview`)
        .then((r) => r.data)
      // A 2xx is not proof of the shape (empty bodies have reached a queryFn
      // before): no counts is a failed read, never a dialog naming "undefined".
      if (
        typeof data?.articles !== "number" ||
        typeof data?.regulations !== "number" ||
        typeof data?.verdicts !== "number"
      ) {
        throw new Error("approve preview: response carries no counts")
      }
      return data
    },
    enabled: enabled && !!declId,
    staleTime: 0,
    retry: false,
  })
}

export function useApproveDeclaration() {
  const queryClient = useQueryClient()
  const { hasPermission } = useUser()
  const mutation = useMutation({
    mutationFn: async (input: ApproveDeclarationIn): Promise<ApproveResult> => {
      const { declId, acknowledgeOpenQuestions, reason } = input
      return apiClientV2
        .post(
          `/material-compliance/declarations/${declId}/approve`,
          acknowledgeOpenQuestions
            ? { acknowledgeOpenQuestions, reason }
            : undefined,
        )
        .then((r) => r.data)
    },
    onSuccess: (_data, { declId }) => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
      queryClient.invalidateQueries({
        queryKey: ["mc", "declarations", "stats"],
      })
      queryClient.invalidateQueries({
        queryKey: ["mc", "declarations", declId, "audit"],
      })
      // Invalidate article sub-resources so the article detail page shows
      // fresh compliance verdicts, substances, and audit after approval.
      queryClient.invalidateQueries({ queryKey: ["mc", "articles"] })
      // The supplier dashboard's "In Prüfung" tile and its drill-down list read
      // this key. Nothing else in the app invalidates it, so an approval left the
      // tile showing the declaration it just removed (review, wave 3).
      queryClient.invalidateQueries({ queryKey: ["mc", "supplier-dashboard"] })
    },
  })
  // BE requires BOTH review + master_data.edit. Guard on the AND — pick the
  // one the user is missing so the button hides for either gap. Consumers use
  // approve.canRun for visibility.
  const missingKey = !hasPermission(PERMISSIONS.MC_DECLARATIONS_REVIEW)
    ? PERMISSIONS.MC_DECLARATIONS_REVIEW
    : PERMISSIONS.MC_MASTER_DATA_EDIT
  return useGuardedMutation(mutation, missingKey)
}

export function useRemapArticle() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      supplierCode: string
      articleNumber: string
    }): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/${input.declarationId}/articles/remap`,
        {
          supplier_code: input.supplierCode,
          article_number: input.articleNumber,
        },
      )
    },
    onSuccess: (_data, variables) => {
      invalidateDeclaration(queryClient, variables.declarationId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

// ─── List (paginated, filterable — for the supplier-panel active-work section) ──

export interface SupplierDeclarationListItem {
  id: string
  declarationNumber: string | null
  supplierId: string | null
  supplierName: string | null
  receivedAt: string | null
  validUntil: string | null
  processingStatus: "pending" | "processing" | "processed" | "failed"
  lifecycleStatus: LifecycleStatus
  framework: string | null
  /** One canonical `mc_framework_codes` code per label in `framework`'s
   *  comma-joined string, same order — see `Declaration.frameworkCodes` (api/types.ts). */
  frameworkCodes?: string[]
}

export interface DeclarationListParams {
  q?: string
  supplierId?: string
  processingStatus?: string
  /** See ListDeclarationsParams.held. */
  held?: "exclude" | "only"
  limit?: number
  enabled?: boolean
}

export function useDeclarationList(params: DeclarationListParams = {}) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery({
    queryKey: ["mc", "declarations", "list", listParams],
    queryFn: ({ pageParam }) => {
      const { supplierId, processingStatus, ...rest } = listParams
      return apiClientV2
        .get<PaginatedOut<SupplierDeclarationListItem>>(
          "/material-compliance/declarations",
          {
            params: {
              ...rest,
              supplier_id: supplierId,
              processing_status: processingStatus,
              cursor: pageParam ?? undefined,
            },
          },
        )
        .then((r) => ({
          // A 202 (or any 2xx) with an empty body resolves `r.data` to `""`,
          // not the paginated shape — axios treats it as success regardless
          // of body. Callers flatMap `.items` across pages with no per-page
          // null check (see SupplierActiveWorkSection.tsx), so a malformed
          // page crashes the very next `.filter`/`.map` instead of rendering
          // empty. Same fix as mcCursorList (mcQueryHelpers.ts).
          items: r.data?.items ?? [],
          total: r.data?.total ?? 0,
          nextCursor: r.data?.nextCursor ?? null,
        }))
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
  })
}

// Polling hook for processing declarations. Paginated (see F3): each page
// caps at the backend page size, with `fetchNextPage`/`hasNextPage` driving
// the "Load more" button in DeclarationList. `refetchInterval` on an
// infinite query refetches every currently loaded page, so polling for
// in-flight declarations keeps working across however many pages are loaded.
export function useDeclarationsWithPolling(
  params?: ListDeclarationsParams,
  opts?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: ["mc", "declarations", params ?? {}],
    enabled: opts?.enabled ?? true,
    queryFn: ({ pageParam }) =>
      declarationsApiCalls.listDeclarations(params, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages
      if (!pages) return 3_000
      return pages.some((page) =>
        page.items.some(
          (d) =>
            d.lifecycleStatus === "processing" ||
            d.lifecycleStatus === "pending",
        ),
      )
        ? 3_000
        : false
    },
  })
}

/**
 * ONE declaration's row, by id (`GET /{id}`) — the same row the list returns for it
 * (same builder server-side), without paging the list to find it. The review page
 * opens from this when `MC_REVIEW_FAST_OPEN` is on.
 *
 * Keyed `["mc","declarations","row",id]`: under the broad `["mc","declarations"]`
 * prefix (approve, retry and reprocess refresh it), but NOT under the per-declaration
 * `["mc","declarations",id]` prefix every review gesture invalidates — the row does
 * not change with a review click, and the footer's "Saving…" must not wait on it.
 * Polls on its own lifecycle while the extraction is still walking, as the list does.
 * Seeded from any cached list page that already holds the row (Next, the inbox), so
 * opening one from there does not flash the blank shell.
 */
export function declarationRowKey(id: string) {
  return ["mc", "declarations", "row", id] as const
}

export function useDeclarationRow(id: string, opts?: { enabled?: boolean }) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: declarationRowKey(id),
    enabled: (opts?.enabled ?? true) && !!id,
    queryFn: async (): Promise<Declaration> =>
      apiClientV2
        .get(`/material-compliance/declarations/${id}`)
        .then((r) => r.data as Declaration),
    placeholderData: () => cachedListRow(queryClient, id),
    // A missing or foreign id is an answer, not a blip: no retries on a 404, and a
    // short wait before the error view on anything else.
    retry: (failureCount, error) =>
      (error as { response?: { status?: number } })?.response?.status !== 404 &&
      failureCount < 2,
    retryDelay: 500,
    refetchInterval: (query) => {
      const status = query.state.data?.lifecycleStatus
      return status === "processing" || status === "pending" ? 3_000 : false
    },
  })
}

/** The row as any cached declarations-list page already holds it, if one does. */
function cachedListRow(
  queryClient: QueryClient,
  id: string,
): Declaration | undefined {
  for (const [, data] of queryClient.getQueriesData<{
    pages?: { items?: Declaration[] }[]
  }>({ queryKey: ["mc", "declarations"] })) {
    for (const page of data?.pages ?? []) {
      const hit = page?.items?.find((d) => d?.id === id)
      if (hit) return hit
    }
  }
  return undefined
}

/**
 * Re-trigger declaration processing.
 * Works for both 'failed' and 'pending' (stored-but-not-started) declarations.
 * On success, invalidates the declarations list so the row flips to processing.
 */
export function useRetryDeclaration() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (id: string): Promise<Declaration> => {
      return apiClientV2
        .post(`/material-compliance/declarations/${id}/retry`)
        .then((r) => r.data as Declaration)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_RETRY)
}

/** Run the pipeline again from the document, discarding what the last run staged
 *  (TUR-1474). For a finished run the reviewer does not trust — after a pipeline fix,
 *  a model regression, an OCR that was not yet available. Refused (409) once approved
 *  or while any staged row carries a reviewer decision; the dialog toasts the refusal
 *  because the global interceptor only toasts 5xx. */
export function useReextractDeclaration() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (id: string): Promise<Declaration> => {
      return apiClientV2
        .post(`/material-compliance/declarations/${id}/reextract`)
        .then((r) => r.data as Declaration)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_RETRY)
}

/** Reviewer override for an `unfilled_form` or `blank_template` hold (TUR-1211
 *  wave 3, product decision): the detector staged nothing because it read the
 *  document as blank, but it can be wrong, so a reviewer can force the pipeline
 *  to read it anyway rather than only being able to request a new declaration.
 *  Same response shape and the same processing/approval/decision guards as
 *  `/reextract`, plus its own 409 NOT_HELD once the hold is already gone. */
export function useReleaseDeclarationHold() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (id: string): Promise<Declaration> => {
      return apiClientV2
        .post(`/material-compliance/declarations/${id}/release-hold`)
        .then((r) => r.data as Declaration)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_RETRY)
}

/** Soft-delete a wrongly uploaded declaration (409 once approved — the master-data
 *  writes name it as evidence). The list, stats, and panel all read under the
 *  ["mc", "declarations"] prefix, so one invalidation clears them together. The
 *  refusal toast lives here rather than in the panel because the global
 *  interceptor only toasts 5xx — a 409 would otherwise fail silently. */
export function useDeleteDeclaration() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClientV2.delete(`/material-compliance/declarations/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_UPLOAD)
}

export function useAssignDeclarationSupplier() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({
      declId,
      supplierId,
    }: {
      declId: string
      supplierId: string
    }): Promise<void> => {
      await apiClientV2.put(
        `/material-compliance/declarations/${declId}/supplier`,
        { supplierId },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

// ─── Direct requests, shared with the agent chat ──────────────────────────────
//
// The chat's tool executor runs outside React, so it cannot call the hooks above
// — but it must call the SAME endpoints the review screens call, or there would
// be two write paths to keep in step. Each of these is the request half of the
// hook of the same name, and each hook keeps its own cache handling.

/** GET /{id} — the fields a chat write gates on before touching anything. */
export interface DeclarationSubject {
  id: string
  declarationNumber: string | null
  supplier: string | null
  supplierId: string | null
  processingStatus: string
  lifecycleStatus?: LifecycleStatus
  approvedAt?: string | null
}

/**
 * A declaration reference as the canonical DEC-YYYY-NNNN, or null if the text is
 * not one. Mirrors the backend's own `declaration_reference`, loosely on purpose:
 * a number read off a screen and retyped loses its case and spacing first.
 */
export function declarationReference(value: string): string | null {
  const match = /^\s*DECL?[\s-]*(\d{4})[\s-]*(\d+)\s*$/i.exec(value)
  if (!match) return null
  // Strip leading zeros BEFORE padding, matching the backend's `int(seq):04d`.
  // `padStart` alone left "DEC-2026-00042" as-is while the server canonicalized
  // it to DEC-2026-0042, so the same input resolved on one side and missed on
  // the other — the worse half being the client, which is where the writes go.
  return `DEC-${match[1]}-${String(Number(match[2])).padStart(4, "0")}`
}

/**
 * The record id for whichever identifier the model is holding.
 *
 * Every declaration surface shows `declarationNumber` and never the row id, so
 * that is what a person quotes — and what a model repeats after printing it. The
 * write endpoints take the row id only, so without this a perfectly good
 * "DEC-2026-0002" reached the API as a 404 and read as "it does not exist".
 *
 * A UUID-shaped value is passed through untouched: it is already the id, and a
 * lookup would only add a request and a way to fail. Anything that is neither is
 * returned as-is so the endpoint's own refusal is what the user sees.
 */
export async function resolveDeclarationId(value: string): Promise<string> {
  const reference = declarationReference(value)
  if (!reference) return value
  // Deliberately NOT `mcApi.listDeclarations`: that swallows errors into an empty
  // page, which on a write path would turn a network failure into "no such
  // declaration" and send the model looking for a record that exists.
  const { data } = await apiClientV2.get("/material-compliance/declarations", {
    params: { q: reference },
  })
  const items = (data?.items ?? []) as Declaration[]
  const hit = items.find(
    (row) => row.declarationNumber?.toUpperCase() === reference,
  )
  return hit?.id ?? value
}

export async function fetchDeclarationSubject(
  id: string,
): Promise<DeclarationSubject> {
  const { data } = await apiClientV2.get(
    `/material-compliance/declarations/${id}`,
  )
  return data as DeclarationSubject
}

export async function fetchDeclarationStatements(
  id: string,
): Promise<StatementGroup[]> {
  const { data } = await apiClientV2.get(
    `/material-compliance/declarations/${id}/statements`,
  )
  return (data ?? []) as StatementGroup[]
}

export async function fetchDeclarationScope(
  id: string,
): Promise<DeclarationScopeOut> {
  const { data } = await apiClientV2.get(
    `/material-compliance/declarations/${id}/scope`,
  )
  return data as DeclarationScopeOut
}

/**
 * WITHOUT the snake to camel transform, exactly as `useDeclarationReadModel`
 * fetches it: this payload carries meaningful data in its object KEYS
 * (`framework:groupIndex`), which the transform would rewrite into an address
 * nothing can look up.
 */
export async function fetchDeclarationReadModel(
  id: string,
): Promise<DeclarationReadModel> {
  const { data } = await apiClientV2.get(
    `/material-compliance/declarations/${id}/read-model`,
    { skipCaseConversion: true } as AxiosRequestConfig & {
      skipCaseConversion?: boolean
    },
  )
  return data as DeclarationReadModel
}

export async function recordOverrideRequest(
  id: string,
  input: { address: string; kind: string; comment: string },
): Promise<void> {
  await apiClientV2.post(
    `/material-compliance/declarations/${id}/overrides`,
    input,
  )
}

export async function retireOverrideRequest(
  id: string,
  address: string,
): Promise<void> {
  await apiClientV2.post(
    `/material-compliance/declarations/${id}/overrides/retire`,
    { address },
  )
}

/**
 * The EXCLUSION itself. Recording the note is only its reason: the staged row's
 * `rejected` status is what takes the statement out of what approve writes, and
 * it is the one fact both sides of the seam read to decide the disposition is in
 * force — see `StudioScreenA`'s `onExclude`, which makes the same two writes in
 * the same order.
 */
export async function reviewStatementRequest(
  statementId: string,
  action: "accept" | "reject",
): Promise<void> {
  await apiClientV2.post(
    `/material-compliance/declarations/statements/${statementId}/review`,
    { action },
  )
}

export async function remapArticleRequest(
  id: string,
  supplierCode: string,
  articleNumber: string,
): Promise<void> {
  await apiClientV2.post(
    `/material-compliance/declarations/${id}/articles/remap`,
    { supplier_code: supplierCode, article_number: articleNumber },
  )
}

export async function assignDeclarationSupplierRequest(
  id: string,
  supplierId: string,
): Promise<void> {
  await apiClientV2.put(`/material-compliance/declarations/${id}/supplier`, {
    supplierId,
  })
}

export async function retryDeclarationRequest(id: string): Promise<void> {
  await apiClientV2.post(`/material-compliance/declarations/${id}/retry`)
}

/**
 * The list, the stat tiles and the panel — everything under the shared prefix,
 * for a write that changes the ROW rather than the review inside it. Matches
 * `useAssignDeclarationSupplier` and `useRetryDeclaration` exactly; a review
 * gesture uses `invalidateDeclaration` instead, which spares the document.
 *
 * A mounted screen does not refetch on its own, so skipping either reads as the
 * write having failed.
 */
export function invalidateDeclarationRows(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
}
